import React, { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
// Using regular Material UI icons instead of react-feather
import { 
  Settings as SettingsIcon,
  Timeline as LineChartIcon, 
  People as UsersIcon, 
  CalendarToday as CalendarIcon, 
  Star as StarIcon,
  Chat as MessageCircleIcon,
  AccessTime as ClockIcon,
  Close as XIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
  Business as BusinessIcon,
  Subject as SubjectIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  DonutLarge as DonutLargeIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Message as MessageIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  GetApp as GetAppIcon,
  Print as PrintIcon,
  AccessTime as AccessTimeIcon,
  LocalHospital as LocalHospitalIcon,
  Info as InfoIcon,
  DoNotDisturb as NoShowIcon,
  CalendarToday,
  LocationOn,
  Person,
  ArrowForwardIos,
  InfoOutlined,
  DownloadOutlined,
  HistoryOutlined,
  FilterAlt,
  Close,
  Assignment as AssignmentIcon,
  Storage as StorageIcon,
  CloudOff as CloudOffIcon,
  Sync as SyncIcon
} from "@mui/icons-material";
import { 
  Alert,
  AppBar, 
  Avatar,
  Box, 
  Button, 
  Card, 
  CardContent, 
  Chip, 
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider, 
  Drawer, 
  FormControl, 
  FormControlLabel,
  FormLabel,
  Grid, 
  IconButton, 
  InputAdornment, 
  InputLabel, 
  Link, 
  List, 
  ListItem, 
  ListItemButton,
  ListItemIcon,
  ListItemText, 
  Menu, 
  MenuItem, 
  Paper, 
  Radio,
  RadioGroup,
  Select, 
  Snackbar, 
  Stack, 
  Tab, 
  Tabs, 
  TextField, 
  Toolbar, 
  Tooltip, 
  Typography,
  useTheme
} from '@mui/material';
import { styled, alpha } from "@mui/material/styles";
import { format, addDays, isValid, subDays, subMonths, isAfter, isBefore, isSameDay } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';

// Import Chart.js components
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip as ChartTooltip, 
  Legend 
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Import only the components that exist
import Patients from "../components/reuse/dashboard/Patients";
import FeedbackPublic from "../components/reuse/feedback/FeedbackPublic";
import {FeedbackPrivate} from "../components/reuse/feedback/FeedbackPrivate";
import AppointmentFeedbackMonitor from "../components/reuse/dashboard/AppointmentFeedbackMonitor";
import CheckoutFeedbackManager from "../components/reuse/dashboard/CheckoutFeedbackManager";
import InsuranceManager from "../components/reuse/dashboard/InsuranceManager";
import athenaService from "../services/athenaService";
import appointmentCacheService from "../services/appointmentCacheService";
import AppointmentService from "../services/appointmentService";
import AppointmentForm from "../components/AppointmentForm";
import AvailabilityManager from "../components/AvailabilityManager";
import AppointmentDetails from "../components/reuse/dashboard/AppointmentDetails";
import dbInitService from "../services/dbInitService";
import LocationsManager from "../components/LocationsManager";
import TeamManager from "../components/TeamManager";
import AdminReviewsPage from "../pages/AdminReviewsPage";
import ConsultationDashboard from "../components/ConsultationDashboard";
import ReputationChartsPublic from "../components/reuse/analytics/ReputationChartsPublic";
import ReputationChartsPrivate from "../components/reuse/analytics/ReputationChartsPrivate";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

// Initialize sample appointment IDs if none exist
const initSampleAppointmentIds = () => {
  // Always use the provided real appointment IDs
  const realAppointmentIds = ["416782","475500","475443","475472","475501","416783","417151","461327","475473","475531","416784","416785","416993","416786","416787","417027","475453","416788","475512","416789","416791","417063","417033"];
  
  localStorage.setItem("appointmentIds", JSON.stringify(realAppointmentIds));
  console.log("Initialized appointment IDs in localStorage:", realAppointmentIds);
};

// Call initialization function immediately
initSampleAppointmentIds();


// Helper function to format provider names consistently
const formatProviderName = (provider) => {
  const firstName = provider.firstname || '';
  const lastName = provider.lastname || '';
  const degree = provider.providertypeid || '';
  let formattedName = `${firstName} ${lastName}`;
  if (degree) formattedName += `, ${degree}`;
  return formattedName;
};
// Styled components
const SidebarContainer = styled(Box)(({ theme }) => ({
  width: '240px',
  height: '100vh',
  position: 'fixed',
  left: 0,
  top: 0,
  backgroundColor: '#fff',
  borderRight: '1px solid #e0e0e0',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1200,
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  marginLeft: '240px',
  minHeight: '100vh',
  width: 'calc(100% - 240px)',
  backgroundColor: theme.palette.background.default,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
}));

const NavItem = styled(NavLink)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 24px',
  color: theme.palette.text.primary,
  textDecoration: 'none',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '&.active': {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

const RouteContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  width: '100%',
  height: '100%',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
}));

const BrandContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '20px 24px',
  position: 'relative',
  borderBottom: '1px solid #e0e0e0',
}));

const UserContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  borderTop: '1px solid #e0e0e0',
  marginTop: 'auto',
  width: '100%',
  maxWidth: '240px',
  boxSizing: 'border-box',
}));

// Create an improved Insights component with real data
const Insights = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [chartPeriod, setChartPeriod] = useState('yearly');
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [useCache, setUseCache] = useState(true);
  const [cacheStats, setCacheStats] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // For week/month selection
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedWeekStart, setSelectedWeekStart] = useState(new Date());


  // Add a refresh function that can be called manually
  const refreshStats = () => {
    setLoading(true);
    setRefreshTrigger(prev => prev + 1);
  };

  // Trigger manual cache sync
  const handleSyncCache = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/sync-appointments', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Cache sync completed successfully');
        // Refresh the data after sync
        setRefreshTrigger(prev => prev + 1);
      } else {
        console.error('❌ Cache sync failed:', result.message);
      }
    } catch (error) {
      console.error('❌ Error triggering cache sync:', error);
    } finally {
      setSyncing(false);
    }
  };

  // Handler for chart period changes with loading state
  const handleChartPeriodChange = (newPeriod) => {
    setChartLoading(true);
    setChartPeriod(newPeriod);
  };

  useEffect(() => {
    const fetchInsightsData = async () => {
      try {
        let totalPatients = 0;
        let todaysAppointments = 0;
        let newPatients = 0;
        let usingCache = false;

        // Try to use cached stats first (instant!)
        if (useCache) {
          console.log("Attempting to load from cache...");
          const cacheStatus = await appointmentCacheService.isCacheReady();
          
          if (cacheStatus.ready && cacheStatus.stats) {
            console.log("Cache is ready! Using pre-computed stats (instant)...");
            
            // Use pre-computed stats directly - NO need to fetch 24k appointments!
            totalPatients = cacheStatus.stats.total_patients || 0;
            todaysAppointments = cacheStatus.stats.appointments_today || 0;
            newPatients = cacheStatus.stats.new_patients_30d || 0;
            
            setCacheStats(cacheStatus.stats);
            usingCache = true;
            console.log(`📊 Stats from cache:`, {
              totalPatients,
              todaysAppointments, 
              newPatients
            });
          } else {
            console.log("Cache not ready, falling back to API");
          }
        }

        // Fallback to API if cache not available or disabled
        if (!usingCache) {
          console.log("Fetching from Athena API for stats calculation...");
          const options = {
            departmentId:1,
            limit:1000,
            showpatientdetail: true 
          };
          
          // Use a 30-day lookback period for statistics
          const pastDate = new Date();
          pastDate.setDate(pastDate.getDate() - 30);
          options.scheduledstartdate = format(pastDate, 'MM/dd/yyyy');
          
          // Use current date plus 30 days for future appointments
          const futureDate = new Date();
          futureDate.setDate(futureDate.getDate() + 30);
          options.scheduledenddate = format(futureDate, 'MM/dd/yyyy');
          
          const appointmentsResponse = await athenaService.getBookedAppointments(options);
          
          let allAppointments = [];
          if (Array.isArray(appointmentsResponse)) {
            allAppointments = appointmentsResponse;
          } else if (appointmentsResponse && appointmentsResponse.appointments) {
            allAppointments = appointmentsResponse.appointments;
          }
          
          // Calculate stats from fetched appointments
          const uniquePatientIds = new Set();
          allAppointments.forEach(apt => {
            if (apt.patient && apt.patient.patientid) {
              uniquePatientIds.add(apt.patient.patientid);
            } else if (apt.patientid) {
              uniquePatientIds.add(apt.patientid);
            }
          });
          totalPatients = uniquePatientIds.size;
          
          const today = new Date();
          const todayString = format(today, 'MM/dd/yyyy');
          todaysAppointments = allAppointments.filter(apt => {
            const aptDate = apt.date.replace(/\\/g, '');
            return aptDate === todayString;
          }).length;
          
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          const newPatientIds = new Set();
          allAppointments.forEach(apt => {
            if (apt.patient && apt.patient.patientid && apt.patient.registrationdate) {
              const regDateStr = apt.patient.registrationdate.replace(/\\/g, '');
              const dateParts = regDateStr.split('/');
              if (dateParts.length === 3) {
                const month = parseInt(dateParts[0]) - 1;
                const day = parseInt(dateParts[1]);
                const year = parseInt(dateParts[2]);
                const registrationDate = new Date(year, month, day);
                if (registrationDate > thirtyDaysAgo) {
                  newPatientIds.add(apt.patient.patientid);
                }
              }
            }
          });
          newPatients = newPatientIds.size;
        }
        
        // Calculate average feedback rating from actual feedback data
        let feedbackRating = "4.8"; // Default fallback
        try {
          const feedbackResponse = await AppointmentService.getFeedbackStats();
          if (feedbackResponse && feedbackResponse.averageRating) {
            feedbackRating = feedbackResponse.averageRating.toFixed(1);
          }
        } catch (error) {
          console.log("Error fetching feedback data, using fallback:", error);
        }
        
        setStats([
          { 
            title: "Total Patients", 
            value: totalPatients, 
            icon: <UsersIcon />, 
            color: theme.palette.primary.main 
          },
          { 
            title: "Appointments Today", 
            value: todaysAppointments, 
            icon: <ClockIcon />, 
            color: "#4CAF50" 
          },
          { 
            title: "New Patients (30d)", 
            value: newPatients, 
            icon: <TrendingUpIcon />, 
            color: "#FF9800" 
          },
          { 
            title: "Feedback Rating", 
            value: feedbackRating, 
            icon: <StarIcon />, 
            color: "#9C27B0" 
          }
        ]);
        
        // Fetch pre-computed chart data from backend (instant!)
        let labels = [];
        let data = [];
        
        if (chartPeriod === 'weekly') {
          const weekStartStr = format(selectedWeekStart, 'yyyy-MM-dd');
          console.log(`Fetching weekly chart data for week starting: ${weekStartStr}`);
          
          const chartData = await appointmentCacheService.getWeeklyChartData(weekStartStr);
          labels = chartData.map(d => d.day_label);
          data = chartData.map(d => d.appointment_count);
          
          console.log('Weekly chart data:', { labels, data });
        } else if (chartPeriod === 'monthly') {
          const year = selectedMonth.getFullYear();
          const month = selectedMonth.getMonth() + 1;
          console.log(`Fetching monthly chart data for: ${year}-${month}`);
          
          const chartData = await appointmentCacheService.getMonthlyChartData(year, month);
          // Ensure we have 5 weeks with proper labels
          labels = ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'];
          data = [0, 0, 0, 0, 0];
          chartData.forEach(d => {
            const weekNum = parseInt(d.week_label.replace('Week ', '')) - 1;
            if (weekNum >= 0 && weekNum < 5) {
              data[weekNum] = d.appointment_count;
            }
          });
          
          console.log('Monthly chart data:', { labels, data });
        } else {
          // Yearly view
          console.log('Fetching yearly chart data...');
          
          const chartData = await appointmentCacheService.getYearlyChartData();
          labels = chartData.map(d => d.month_label);
          data = chartData.map(d => d.appointment_count);
          
          console.log('Yearly chart data:', { labels, data });
        }
        
        // Set chart data
        setChartData({
          labels,
          datasets: [{
            label: 'Appointments',
            data,
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.dark,
            borderWidth: 1,
          }]
        });
        
      } catch (error) {
        console.error("Error fetching insights data:", error);
      } finally {
        setLoading(false);
        // Add small delay for smooth transition
        setTimeout(() => setChartLoading(false), 300);
      }
    };

    fetchInsightsData();
  }, [refreshTrigger, chartPeriod, useCache, selectedMonth, selectedWeekStart]);

  // We'll use an overlay loading indicator instead of replacing the entire UI

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', pb: 5, position: 'relative' }}>
      {/* Loading overlay that keeps UI visible */}
      {loading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">Loading insights data...</Typography>
        </Box>
      )}
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          p: 3,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: theme.palette.background.paper,
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{
              bgcolor: theme.palette.secondary.main,
              color: theme.palette.primary.main,
              width: 30,
              height: 30,
              mr: 2
            }}
          >
            <LineChartIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
              Insights
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: theme.palette.text.secondary
              }}
            >
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {cacheStats && (
            <Chip
              icon={<StorageIcon />}
              label={`Cache: ${cacheStats.total_cached} appointments`}
              color="success"
              variant="outlined"
              size="small"
            />
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={useCache ? <StorageIcon /> : <CloudOffIcon />}
            onClick={() => setUseCache(!useCache)}
            sx={{ mr: 1 }}
          >
            {useCache ? 'Using Cache' : 'Using API'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSyncCache}
            disabled={syncing || !useCache}
            sx={{ mr: 1 }}
          >
            {syncing ? 'Syncing...' : 'Sync Cache'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={refreshStats}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      </Paper>

      {/* Content wrapper with padding */}
      <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {stats.map((stat, index) => (
          <Grid item xs={12} md={6} lg={3} key={index}>
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                bgcolor: theme.palette.background.paper,
                height: '100%'
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    color: stat.color,
                    width: 56,
                    height: 56,
                    mr: 2
                  }}
                >
                  {stat.icon}
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: stat.color }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {stat.title}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              bgcolor: theme.palette.background.paper,
              p: 3
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                Appointment Statistics
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button 
                  variant={chartPeriod === 'weekly' ? "contained" : "outlined"} 
                  size="small" 
                  sx={{ borderRadius: 4 }}
                  onClick={() => handleChartPeriodChange('weekly')}
                  disabled={chartLoading}
                  startIcon={chartLoading && chartPeriod === 'weekly' ? <CircularProgress size={14} /> : null}
                >Weekly</Button>
                <Button 
                  variant={chartPeriod === 'monthly' ? "contained" : "outlined"} 
                  size="small" 
                  sx={{ borderRadius: 4 }}
                  onClick={() => handleChartPeriodChange('monthly')}
                  disabled={chartLoading}
                  startIcon={chartLoading && chartPeriod === 'monthly' ? <CircularProgress size={14} /> : null}
                >Monthly</Button>
                <Button 
                  variant={chartPeriod === 'yearly' ? "contained" : "outlined"} 
                  size="small" 
                  sx={{ 
                    borderRadius: 4,
                    bgcolor: chartPeriod === 'yearly' ? theme.palette.primary.main : 'transparent' 
                  }}
                  onClick={() => handleChartPeriodChange('yearly')}
                  disabled={chartLoading}
                  startIcon={chartLoading && chartPeriod === 'yearly' ? <CircularProgress size={14} /> : null}
                >
                  Yearly
                </Button>

                {/* Date pickers for week/month selection */}
                {chartPeriod === 'weekly' && (
                  <TextField
                    type="date"
                    size="small"
                    label="Week Start"
                    value={format(selectedWeekStart, 'yyyy-MM-dd')}
                    onChange={(e) => {
                      setChartLoading(true);
                      setSelectedWeekStart(new Date(e.target.value));
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ 
                      width: 150,
                      '& .MuiInputBase-input': { fontSize: '0.875rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                    }}
                  />
                )}
                {chartPeriod === 'monthly' && (
                  <TextField
                    type="month"
                    size="small"
                    label="Month"
                    value={format(selectedMonth, 'yyyy-MM')}
                    onChange={(e) => {
                      setChartLoading(true);
                      setSelectedMonth(new Date(e.target.value + '-01'));
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ 
                      width: 135,
                      '& .MuiInputBase-input': { 
                        fontSize: '0.75rem',
                        padding: '6px 8px'
                      },
                      '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                    }}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ height: 250, width: '100%', position: 'relative' }}>
              {chartLoading && (
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: 1
                }}>
                  <CircularProgress size={40} sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Loading {chartPeriod} data...
                  </Typography>
                </Box>
              )}
              {chartData ? (
                <Bar
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {
                      duration: 750,
                      easing: 'easeInOutQuart'
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: { precision: 0 }
                      }
                    }
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <BarChartIcon sx={{ fontSize: 120, color: 'rgba(0, 0, 0, 0.1)' }} />
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Reputation Analytics Section */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.warning.main,
                color: 'white',
                width: 30,
                height: 30,
                mr: 2
              }}
            >
              <StarIcon fontSize="small" />
            </Avatar>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
              Reputation Analytics
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              bgcolor: theme.palette.background.paper,
              p: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.main,
                  width: 32,
                  height: 32,
                  mr: 1.5
                }}
              >
                <StarIcon fontSize="small" />
              </Avatar>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                Public Reputation
              </Typography>
            </Box>
            <ReputationChartsPublic />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              bgcolor: theme.palette.background.paper,
              p: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  bgcolor: theme.palette.secondary.main,
                  width: 32,
                  height: 32,
                  mr: 1.5
                }}
              >
                <MessageCircleIcon fontSize="small" />
              </Avatar>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                Private Feedback
              </Typography>
            </Box>
            <ReputationChartsPrivate />
          </Paper>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

  // Create Appointments component with real data matching the provided design
const Appointments = () => {
  // State declarations (all grouped at the top)
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [rawAppointments, setRawAppointments] = useState([]); // Store original unformatted appointments
  const [appointmentMap, setAppointmentMap] = useState({}); // Map between formatted and raw appointments
  const [loading, setLoading] = useState(false);
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [patientSearch, setPatientSearch] = useState(''); // Patient search field
  const [openAppointmentForm, setOpenAppointmentForm] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [cancelReasons, setCancelReasons] = useState([]);
  const [selectedCancelReasonId, setSelectedCancelReasonId] = useState('');
  const [customCancelReason, setCustomCancelReason] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [loadingReasons, setLoadingReasons] = useState(false);
  
  // Reschedule states
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [appointmentToReschedule, setAppointmentToReschedule] = useState(null);
  const [hasInitializedReschedule, setHasInitializedReschedule] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedRescheduleDate, setSelectedRescheduleDate] = useState(new Date());
  const [selectedRescheduleDepartment, setSelectedRescheduleDepartment] = useState('');
  const [selectedRescheduleProvider, setSelectedRescheduleProvider] = useState('');
  const [loadingRescheduleProviders, setLoadingRescheduleProviders] = useState(false);
  const [rescheduleProviders, setRescheduleProviders] = useState([]);
  const [confirmRescheduleLoading, setConfirmRescheduleLoading] = useState(false);
  const [confirmCancelLoading, setConfirmCancelLoading] = useState(false);
  
  // Departments, providers, and filters state
  const [departments, setDepartments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('1'); // Default to department 1
  const [selectedProvider, setSelectedProvider] = useState('');
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  
  // Location grouping states
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [departmentsByLocation, setDepartmentsByLocation] = useState({});
  
  // Create ref at component top level
  const isInitialRender = useRef(true);
  
  // Progressive loading state and refs
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const fetchSessionRef = useRef(0); // Session ID to track which fetch is active
  
  // Progressive loading constants
  const MAX_RECORDS = 2000;
  const BATCH_SIZE = 200;
  const BUFFER_FLUSH_SIZE = 400;
  
  const theme = useTheme();
  const navigate = useNavigate();

  // Filter appointments based on patient search
  // Initial data fetching and check for opening appointment form directly
  useEffect(() => {
    fetchAppointments();
    
    // Check if we should open the appointment form directly (from Patients tab)
    const openFormDirectly = sessionStorage.getItem('openAppointmentFormDirectly');
    if (openFormDirectly === 'true') {
      // Open the appointment form
      setOpenAppointmentForm(true);
      // Clear the flag so it doesn't reopen on future navigation
      sessionStorage.removeItem('openAppointmentFormDirectly');
    }
  }, []);

  // Filter appointments based on patient search
  useEffect(() => {
    if (appointments.length > 0) {
      if (!patientSearch) {
        // If no search query, show all appointments
        setFilteredAppointments(appointments);
      } else {
        // Filter appointments by patient name or ID
        const searchTerm = patientSearch.toLowerCase().trim();
        console.log("Search term: " + searchTerm);

        const filtered = appointments.filter(apt => {
          // Check if this is a raw appointment with patient object
          if (apt.patient) {
            console.log("appointment"+JSON.stringify(apt));
            console.log("appointment patient"+JSON.stringify(apt.patient));

            // Get patient name from patient object
            const patientName = `${apt.patient.name || ""}`.toLowerCase();
            const patientId = apt.patient.id ? apt.patient.id.toString().toLowerCase() : '';
            
            console.log("Patient name from patient object: " + patientName);
            return patientName.includes(searchTerm) || patientId.includes(searchTerm);
          }
          
          // Check if this is a formatted appointment with patientName/patientId already extracted
          if (apt.patientName) {
            const patientName = apt.patientName.toLowerCase();
            const patientId = apt.patientId ? apt.patientId.toString().toLowerCase() : '';
            
            return patientName.includes(searchTerm) || patientId.includes(searchTerm);
          }
          
          // If we reach here, no patient data was found
          return false;
        });
        
        setFilteredAppointments(filtered);
      }
    } else {
      setFilteredAppointments([]);
    }
  }, [appointments, patientSearch]);

  // Helper function to process appointments data
  // mergeMode: if true, merges with existing appointments instead of replacing
  const processAppointmentsData = useCallback((appointmentsData, allProvidersList, mergeMode = false) => {
    // Check if appointments data is in the expected format
    const hasAppointments = appointmentsData && (
      (Array.isArray(appointmentsData) && appointmentsData.length > 0) || 
      (appointmentsData.scheduled && appointmentsData.scheduled.length > 0) ||
      (appointmentsData.cancelled && appointmentsData.cancelled.length > 0) ||
      (appointmentsData.rescheduled && appointmentsData.rescheduled.length > 0)
    );
    
    if (hasAppointments) {
      // Get appointments from the appropriate structure
      let appointments = [];
      if (Array.isArray(appointmentsData)) {
        appointments = appointmentsData;
      } else {
        // Combine all appointment types
        appointments = [
          ...(appointmentsData.scheduled || []),
          ...(appointmentsData.cancelled || []),
          ...(appointmentsData.rescheduled || [])
        ];
      }
      
      console.log(`Processing ${appointments.length} appointments... (mergeMode: ${mergeMode})`);
      
      // Store the raw appointments (merge or replace based on mode)
      if (mergeMode) {
        setRawAppointments(prev => {
          const map = new Map(prev.map(a => [a.appointmentid, a]));
          appointments.forEach(a => map.set(a.appointmentid, a));
          const merged = Array.from(map.values());
          setTotalAppointments(merged.length);
          return merged;
        });
      } else {
        setRawAppointments(appointments);
        setTotalAppointments(appointments.length);
      }
      
      // Create a mapping object to link formatted and raw appointments
      const newAppointmentMap = {};
      
      // Format appointments for display
      const formattedAppointments = appointments.map((apt, index) => {
        console.log(`Processing appointment ${index + 1}/${appointments.length}:`, apt);
          
        // Process date and time
        const appointmentDate = new Date(apt.date);
        console.log("Appointment date parsed:", appointmentDate);
          
        // Format date
        const formattedDate = appointmentDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        });
        console.log("Formatted date:", formattedDate);
          
        // Extract time
        let formattedTime = apt.starttime || '';
        console.log("Original start time:", formattedTime);
          
        if (!formattedTime && appointmentDate instanceof Date && !isNaN(appointmentDate)) {
          const hours = appointmentDate.getHours();
          const minutes = appointmentDate.getMinutes();
          const ampm = hours >= 12 ? 'PM' : 'AM';
          const formattedHours = hours % 12 || 12;
          const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
          formattedTime = `${formattedHours}:${formattedMinutes} ${ampm}`;
          console.log("Generated time from date:", formattedTime);
        }
          
        // Determine if new patient
        const isNewPatient = !apt.patient?.lastappointment;
        console.log("Is new patient?", isNewPatient);
          
        // Map appointment status
        let status = "Booked";
        if (apt.appointmentstatus) {
          // Handle athenaNet status codes
          switch(apt.appointmentstatus) {
            case 'x':
              // Check if the cancelreasonname is PATIENT NO SHOW
              if (apt.cancelreasonname === 'PATIENT NO SHOW') {
                status = "No Show";
              } else {
                status = "Cancelled";
              }
              break;
            case 'f': 
              status = "Scheduled";
              break;
            case 'o':
              status = "Open";
              break;
            case '2':
              status = "Checked In";
              break;
            case '3':
              status = "Checked Out";
              break;
            case '4':
              status = "Charge Entered";
              break;
            default:
              // Handle string status descriptions for backward compatibility
              if (apt.appointmentstatus === 'cancelled') status = "Cancelled";
              else if (apt.appointmentstatus === 'completed') status = "Completed";
              else if (apt.appointmentstatus === 'checked-in') status = "Checked In";
              else status = "Booked";
          }
        } else {
          // Fallback to boolean flags for compatibility
          if (apt.cancelled) status = "Cancelled";
          else if (apt.completed) status = "Completed";
          else if (apt.checkedin) status = "Checked In";
        }
        console.log("Appointment status:", status);
        
        // Extract appointment notes (handle both formats: string or array of objects)
        let notesText = 'No reason specified';
        if (apt.appointmentnotes) {
          if (Array.isArray(apt.appointmentnotes)) {
            // Handle notes array with objects that have a text property
            const notesArray = apt.appointmentnotes.map(note => 
              typeof note === 'object' && note.text ? note.text : note
            );
            notesText = notesArray.join(', ');
          } else if (typeof apt.appointmentnotes === 'string') {
            // Handle string notes
            notesText = apt.appointmentnotes;
          } else if (typeof apt.appointmentnotes === 'object' && apt.appointmentnotes.text) {
            // Handle single object with text property
            notesText = apt.appointmentnotes.text;
          }
        } else if (apt.notes) {
          notesText = apt.notes;
        }
          
        // Map provider information properly using the pre-loaded providers list
        let providerName = 'No provider specified';
        if (apt.provider && apt.provider.name) {
          providerName = apt.provider.name;
        } else if (apt.providerid) {
          // First check current department providers
          const provider = providers.find(p => p.providerid === apt.providerid || p.providerid === parseInt(apt.providerid));
          if (provider) {
            // Special case for IV providers that use displayname instead of first/last name
            if (provider.displayname === 'IV_OFFICE') {
              providerName = 'IV Office';
            } else if (provider.displayname === 'IV_HOME') {
              providerName = 'IV Home';
            } else {
              providerName = `${provider.firstname || ''} ${provider.lastname || ''}`.trim();
              if (!providerName) {
                providerName = provider.displayname || 'No provider name';
              }
            }
          } else {
            // Then check all providers list
            const allProvider = allProvidersList.find(p => p.providerid === apt.providerid || p.providerid === parseInt(apt.providerid));
            if (allProvider) {
              // Special case for IV providers that use displayname instead of first/last name
              if (allProvider.displayname === 'IV_OFFICE') {
                providerName = 'IV Office';
              } else if (allProvider.displayname === 'IV_HOME') {
                providerName = 'IV Home';
              } else {
                providerName = `${allProvider.firstname || ''} ${allProvider.lastname || ''}`.trim();
                if (!providerName) {
                  providerName = allProvider.displayname || 'No provider name';
                }
              }
            }
          }
        }
        
        // Map department information properly
        let location = 'Main Office';
        if (apt.department && apt.department.name) {
          location = apt.department.name;
        } else if (apt.departmentid) {
          // Try to find department name from the departments list
          const department = departments.find(d => d.departmentid === apt.departmentid || d.departmentid === parseInt(apt.departmentid));
          if (department) {
            location = department.name;
          }
        }
        
        const formattedAppointment = {
          id: apt.appointmentid || apt.id,
          date: formattedDate,
          time: formattedTime,
          patient: {
            id: apt.patient?.patientid || apt.patientid,
            name: apt.patient ? `${apt.patient.firstname} ${apt.patient.lastname}` : 'Unknown Patient',
            email: apt.patient?.email || '',
            phone: apt.patient?.mobilephone || apt.patient?.homephone || '',
            isNew: isNewPatient
          },
          receivedDate: apt.receiveddate ? new Date(apt.receiveddate).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
          }) : null,
          requestedDate: apt.requesteddate ? new Date(apt.requesteddate).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
          }) : null,
          providerName: providerName,
          location: location,
          status: status,
          reason: notesText,
          platform: apt.platform || 'AthenaHealth'
        };
        
        // Add to the mapping between formatted and raw appointments
        newAppointmentMap[formattedAppointment.id] = index;
          
        console.log("Formatted appointment:", formattedAppointment);
        return formattedAppointment;
      });
        
      console.log("Sorting appointments by date/time...");
      // Sort appointments by date/time
      formattedAppointments.sort((a, b) => {
        const dateA = new Date(a.date + ' ' + a.time);
        const dateB = new Date(b.date + ' ' + b.time);
        console.log(`Comparing: ${a.date} ${a.time} vs ${b.date} ${b.time}`);
          
        if (isNaN(dateA) || isNaN(dateB)) {
          console.log("Invalid date encountered, falling back to string comparison");
          return a.date.localeCompare(b.date);
        }
        return dateA - dateB;
      });
        
      console.log("Final formatted appointments:", formattedAppointments);
      
      // Set appointments (merge or replace based on mode)
      if (mergeMode) {
        setAppointments(prev => {
          const map = new Map(prev.map(a => [a.id, a]));
          formattedAppointments.forEach(a => map.set(a.id, a));
          const merged = Array.from(map.values());
          // Re-sort merged appointments
          merged.sort((a, b) => {
            const dateA = new Date(a.date + ' ' + a.time);
            const dateB = new Date(b.date + ' ' + b.time);
            if (isNaN(dateA) || isNaN(dateB)) {
              return a.date.localeCompare(b.date);
            }
            return dateA - dateB;
          });
          return merged;
        });
        setAppointmentMap(prev => ({ ...prev, ...newAppointmentMap }));
      } else {
        setAppointments(formattedAppointments);
        setAppointmentMap(newAppointmentMap);
      }
    } else if (!mergeMode) {
      console.warn("No appointments returned from athenaService");
      setAppointments([]);
    }
  }, [departments, providers]);

  // Helper function to merge appointments with deduplication by ID
  const mergeAppointments = useCallback((incoming, allProvidersList) => {
    // Merge raw appointments with deduplication
    setRawAppointments(prev => {
      const map = new Map(prev.map(a => [a.appointmentid, a]));
      incoming.forEach(a => map.set(a.appointmentid, a));
      return Array.from(map.values());
    });
    
    // Process and merge formatted appointments
    processAppointmentsData(incoming, allProvidersList, true); // true = merge mode
  }, [processAppointmentsData]);

  // Sequential background loading function
  const loadRemainingSequentially = useCallback(async (startOffset, baseOptions, allProvidersList, sessionId) => {
    // Only continue if this is still the active session
    if (sessionId !== fetchSessionRef.current) {
      console.log('[Progressive Load] Session expired, aborting');
      return;
    }
    
    setIsLoadingMore(true);
    
    let buffer = [];
    const MAX_RETRIES = 2;
    
    // Full date range for remaining appointments (15 days past, 30 days future)
    const pastDate = new Date();
    pastDate.setDate(pastDate.getDate() - 15);
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 30);
    
    try {
      // Check if we're loading from multiple departments (location mode)
      if (baseOptions.locationDepts && baseOptions.locationDepts.length > 0) {
        // Location mode: fetch remaining from each department
        console.log(`[Progressive Load] Location mode: loading from ${baseOptions.locationDepts.length} departments`);
        
        for (const dept of baseOptions.locationDepts) {
          if (sessionId !== fetchSessionRef.current) {
            console.log('[Progressive Load] Session expired during location load, aborting');
            break;
          }
          
          let deptOffset = 100; // Start after first batch
          let retryCount = 0;
          
          const deptOptions = {
            showpatientdetail: true,
            departmentId: parseInt(dept.departmentid),
            scheduledstartdate: format(pastDate, 'MM/dd/yyyy'),
            scheduledenddate: format(futureDate, 'MM/dd/yyyy')
          };
          
          if (baseOptions.providerId) {
            deptOptions.providerId = baseOptions.providerId;
          }
          
          // Load remaining for this department
          while (sessionId === fetchSessionRef.current && deptOffset < MAX_RECORDS) {
            try {
              console.log(`[Progressive Load] Dept ${dept.departmentid}: offset ${deptOffset}, limit ${BATCH_SIZE}`);
              
              const batch = await athenaService.getBookedAppointments({
                ...deptOptions,
                limit: BATCH_SIZE,
                offset: deptOffset
              });
              
              retryCount = 0;
              
              if (!batch?.length) {
                console.log(`[Progressive Load] Dept ${dept.departmentid}: no more appointments`);
                break;
              }
              
              buffer.push(...batch);
              deptOffset += batch.length;
              setLoadedCount(prev => prev + batch.length);
              
              // Flush buffer
              if (buffer.length >= BUFFER_FLUSH_SIZE) {
                console.log(`[Progressive Load] Flushing buffer of ${buffer.length} appointments`);
                mergeAppointments(buffer, allProvidersList);
                buffer = [];
              }
              
              if (batch.length < BATCH_SIZE) break;
              
            } catch (batchError) {
              console.error(`[Progressive Load] Dept ${dept.departmentid} error:`, batchError);
              retryCount++;
              if (retryCount >= MAX_RETRIES) break;
              await new Promise(r => setTimeout(r, 1000));
            }
          }
        }
      } else {
        // Single department mode
        let offset = startOffset;
        let retryCount = 0;
        
        const fullRangeOptions = {
          ...baseOptions,
          scheduledstartdate: format(pastDate, 'MM/dd/yyyy'),
          scheduledenddate: format(futureDate, 'MM/dd/yyyy')
        };
        
        while (sessionId === fetchSessionRef.current && offset < MAX_RECORDS) {
          try {
            console.log(`[Progressive Load] Fetching batch at offset ${offset}, limit ${BATCH_SIZE}`);
            
            const batch = await athenaService.getBookedAppointments({
              ...fullRangeOptions,
              limit: BATCH_SIZE,
              offset
            });
            
            retryCount = 0;
            
            if (!batch?.length) {
              console.log('[Progressive Load] No more appointments, stopping');
              break;
            }
            
            buffer.push(...batch);
            offset += batch.length;
            setLoadedCount(prev => prev + batch.length);
            
            console.log(`[Progressive Load] Received ${batch.length} appointments, buffer size: ${buffer.length}`);
            
            if (buffer.length >= BUFFER_FLUSH_SIZE) {
              console.log(`[Progressive Load] Flushing buffer of ${buffer.length} appointments`);
              mergeAppointments(buffer, allProvidersList);
              buffer = [];
            }
            
            if (batch.length < BATCH_SIZE) {
              console.log('[Progressive Load] Last batch received, stopping');
              break;
            }
            
          } catch (batchError) {
            console.error(`[Progressive Load] Batch error at offset ${offset}:`, batchError);
            retryCount++;
            if (retryCount >= MAX_RETRIES) {
              console.warn('[Progressive Load] Max retries reached, stopping');
              break;
            }
            await new Promise(r => setTimeout(r, 1000));
          }
        }
      }
    } finally {
      // Flush remaining buffer
      if (buffer.length > 0) {
        console.log(`[Progressive Load] Final flush of ${buffer.length} appointments`);
        mergeAppointments(buffer, allProvidersList);
      }
      setIsLoadingMore(false);
      console.log('[Progressive Load] Background loading complete');
    }
  }, [mergeAppointments, MAX_RECORDS, BATCH_SIZE, BUFFER_FLUSH_SIZE]);

  const fetchAppointmentsWithProviders = useCallback(async (allProvidersList) => {
    console.log("===== Appointments Component: Starting fetch (Progressive Loading) =====");
    
    // Increment session ID to invalidate any ongoing background loads
    const currentSession = ++fetchSessionRef.current;
    console.log(`[Progressive Load] Starting new session: ${currentSession}`);
    setLoadedCount(0);
    
    try {
      // Base options for all API calls
      let baseOptions = {
        showpatientdetail: true
      };
      
      // Add provider filter if selected
      if (selectedProvider) {
        baseOptions.providerId = parseInt(selectedProvider);
      }
      
      // Date range for first batch
      const today = new Date();
      const tenDaysLater = new Date();
      tenDaysLater.setDate(today.getDate() + 10);
      
      let allFirstBatchAppointments = [];
      
      // Determine department(s) to fetch from
      if (selectedDepartment !== '' && selectedDepartment !== '1') {
        // Specific department selected - single fetch
        baseOptions.departmentId = parseInt(selectedDepartment);
        
        const firstBatchOptions = {
          ...baseOptions,
          scheduledstartdate: format(today, 'MM/dd/yyyy'),
          scheduledenddate: format(tenDaysLater, 'MM/dd/yyyy'),
          limit: 100,
          offset: 0
        };
        
        console.log("[Progressive Load] STEP 1: Fast first batch for single department:", firstBatchOptions);
        const firstBatch = await athenaService.getBookedAppointments(firstBatchOptions);
        allFirstBatchAppointments = firstBatch || [];
        
      } else if (selectedLocation && departmentsByLocation[selectedLocation]) {
        // Location selected with "All Departments" - fetch from all departments in location
        const locationDepts = departmentsByLocation[selectedLocation];
        console.log(`[Progressive Load] STEP 1: Fetching from ${locationDepts.length} departments in ${selectedLocation}`);
        
        // Parallel fetch from all departments in location
        const deptPromises = locationDepts.map(dept => {
          const deptOptions = {
            ...baseOptions,
            departmentId: parseInt(dept.departmentid),
            scheduledstartdate: format(today, 'MM/dd/yyyy'),
            scheduledenddate: format(tenDaysLater, 'MM/dd/yyyy'),
            limit: 100,
            offset: 0
          };
          return athenaService.getBookedAppointments(deptOptions).catch(err => {
            console.error(`Error fetching dept ${dept.departmentid}:`, err);
            return [];
          });
        });
        
        const results = await Promise.all(deptPromises);
        allFirstBatchAppointments = results.flat();
        
        // Store location departments for background loading
        baseOptions.locationDepts = locationDepts;
        
      } else {
        // Default - fetch from department 1 (Glendale)
        baseOptions.departmentId = 1;
        
        const firstBatchOptions = {
          ...baseOptions,
          scheduledstartdate: format(today, 'MM/dd/yyyy'),
          scheduledenddate: format(tenDaysLater, 'MM/dd/yyyy'),
          limit: 100,
          offset: 0
        };
        
        console.log("[Progressive Load] STEP 1: Fast first batch (default dept 1):", firstBatchOptions);
        const firstBatch = await athenaService.getBookedAppointments(firstBatchOptions);
        allFirstBatchAppointments = firstBatch || [];
      }
      
      console.log(`[Progressive Load] First batch received: ${allFirstBatchAppointments.length} appointments`);
      
      // Show first batch immediately!
      processAppointmentsData(allFirstBatchAppointments, allProvidersList);
      setLoadedCount(allFirstBatchAppointments.length);
      setLoading(false); // Stop main loading spinner
      
      // STEP 2: Background sequential loading for remaining appointments
      console.log("[Progressive Load] STEP 2: Starting background sequential loading...");
      loadRemainingSequentially(allFirstBatchAppointments.length, baseOptions, allProvidersList, currentSession);
      
      console.log("[Progressive Load] First batch displayed, background loading started");
    } catch (error) {
      console.error("===== ERROR FETCHING APPOINTMENTS =====");
      console.error("Error details:", error);
      console.error("Stack trace:", error.stack);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not load appointment data';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      // Show error in snackbar
      setSnackbar({
        open: true, 
        message: `Failed to load appointments: ${errorMessage}`,
        severity: 'error'
      });
        
      // Use empty appointments array on error
      setAppointments([]);
      setTotalAppointments(0);
    } finally {
      setLoading(false);
      console.log("===== Appointments Component: Finished loading =====");
    }
  }, [selectedDepartment, selectedProvider, selectedLocation, departmentsByLocation, processAppointmentsData, loadRemainingSequentially]);

  const fetchAppointments = useCallback(async () => {
    try {
      // Get fresh provider data first
      setLoading(true);
      const allProviders = await athenaService.getAllProviders();
      fetchAppointmentsWithProviders(allProviders);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
  }, [fetchAppointmentsWithProviders]);

  // Initial load effect
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        // 1. Load departments
        const depts = await athenaService.getDepartments();
        
        // Filter out departments with empty providerlist
        const filteredDepts = depts.filter(dept => 
          dept.providerlist && dept.providerlist.length > 0
        );
        
        console.log('Appointments: Departments with providers:', filteredDepts);
        setDepartments(filteredDepts || []);
        
        // Group departments by location (city)
        const deptsByLocation = {};
        const locationList = [];
        
        filteredDepts.forEach(dept => {
          const locationName = (dept.city || 'Unknown').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
          
          // Initialize location group if it doesn't exist
          if (!deptsByLocation[locationName]) {
            deptsByLocation[locationName] = [];
            locationList.push({
              name: locationName,
              departments: []
            });
          }
          
          // Add department to its location group
          deptsByLocation[locationName].push(dept);
          
          // Update the location list with this department
          const locationIndex = locationList.findIndex(loc => loc.name === locationName);
          if (locationIndex >= 0) {
            locationList[locationIndex].departments.push(dept);
          }
        });
        
        console.log("Departments grouped by location:", deptsByLocation);
        console.log("Location list:", locationList);
        
        setLocations(locationList);
        setDepartmentsByLocation(deptsByLocation);
        
        // 2. Load ALL providers to have comprehensive provider data
        const allProvs = await athenaService.getAllProviders();
        console.log("Loaded all providers:", allProvs);
        
        // 3. Set department-specific providers if selectedDepartment exists
        if (selectedDepartment) {
          const department = filteredDepts.find(d => 
            d.departmentid === selectedDepartment || 
            d.departmentid === parseInt(selectedDepartment)
          );
          
          if (department && department.providerlist && department.providerlist.length > 0) {
            // First get providers for this department that match the providerlist
            const departmentProvs = await athenaService.getProviders(parseInt(selectedDepartment));
            
            // Then filter to only include those in providerlist
            const filteredProvs = departmentProvs.filter(p => 
              department.providerlist.includes(p.providerid.toString())
            );
            console.log('Initial filtered providers for department:', filteredProvs);
            setProviders(filteredProvs || []);
          } else {
            setProviders([]);
            console.log('No valid department or providerlist available');
          }
        } else {
          setProviders([]);
        }
        
        // 4. Fetch appointments immediately - this will use the allProvs data
        await fetchAppointmentsWithProviders(allProvs);
      } catch (error) {
        console.error("Error loading initial data:", error);
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, []); // Only run once on mount

  // Additional effect for when filters change
  useEffect(() => {
    if (selectedDepartment !== '1') { // Only run when department is explicitly changed
      const updateProviders = async () => {
        try {
          if (selectedDepartment) {
            const department = departments.find(d => 
              d.departmentid === selectedDepartment || 
              d.departmentid === parseInt(selectedDepartment)
            );
            
            if (department && department.providerlist && department.providerlist.length > 0) {
              // First get providers for this department that match the providerlist
              const departmentProvs = await athenaService.getProviders(selectedDepartment);
              
              // Then filter to only include those in providerlist
              const filteredProvs = departmentProvs.filter(p => 
                department.providerlist.includes(p.providerid.toString())
              );
              
              console.log('Filtered providers for department:', filteredProvs);
              setProviders(filteredProvs || []);
            } else {
              setProviders([]);
              console.log('No valid department or providerlist available');
            }
          } else {
            setProviders([]);
          }
        } catch (error) {
          console.error("Error updating providers:", error);
          setProviders([]);
        }
      };
      updateProviders();
    }
  }, [selectedDepartment]);

  // Effect to fetch appointments when filters change (after initial load)
  useEffect(() => {
    // Skip the initial render since we're already loading data there
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    
    // Don't refetch on the initial value (default department selection)
    // Note: fetchAppointments increments session ID which auto-aborts any ongoing background load
    if (selectedDepartment !== '1' || selectedProvider !== '') {
      fetchAppointments();
    }
    // Note: No cleanup needed - session ID is incremented when new fetch starts
  }, [selectedDepartment, selectedProvider, fetchAppointments]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBookAppointment = () => {
    setOpenAppointmentForm(true);
  };
  
  const handleCloseAppointmentForm = () => {
    setOpenAppointmentForm(false);
  };
  
  const handleSaveAppointment = (appointmentData) => {
    // Refresh appointments after booking
    fetchAppointments();
    
    setSnackbar({
      open: true,
      message: 'Appointment booked successfully!',
      severity: 'success'
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Handle location change
  const handleLocationChange = async (event) => {
    setLoading(true);
    const location = event.target.value;
    console.log("Selected location:", location);
    
    setSelectedLocation(location);
    setSelectedDepartment(''); // Empty means 'All Departments'
    setSelectedProvider(''); // Reset provider
    
    // If location is selected, get all providers for departments in this location
    if (location && departmentsByLocation[location]) {
      try {
        // Get all departments for this location
        const locationDepts = departmentsByLocation[location];
        
        // Collect all providers from all departments in this location
        const allLocationProviders = [];
        const providerMap = new Map(); // To track unique providers
        
        // For each department in the location, fetch its providers
        for (const dept of locationDepts) {
          const deptProvs = await athenaService.getProviders(parseInt(dept.departmentid));
          
          // If department has a providerlist, filter providers
          if (dept.providerlist && dept.providerlist.length > 0) {
            const filteredProvs = deptProvs.filter(p => 
              dept.providerlist.includes(p.providerid.toString())
            );
            
            // Add unique providers to the collection
            for (const prov of filteredProvs) {
              if (!providerMap.has(prov.providerid.toString())) {
                providerMap.set(prov.providerid.toString(), true);
                allLocationProviders.push(prov);
              }
            }
          }
        }
        
        setProviders(allLocationProviders);
      } catch (error) {
        console.error("Error loading providers for location:", error);
        setProviders([]);
      }
    } else {
      // No location selected, reset providers
      setProviders([]);
    }
    
    // Fetch appointments after location change
  };

  const handleDepartmentChange = async (event) => {
    const deptId = event.target.value;

    setSelectedDepartment(deptId);
    
    try {
      if (deptId) {
        // Find the selected department to get its providerlist
        const department = departments.find(d => d.departmentid === deptId || d.departmentid === parseInt(deptId));
        console.log("Selected department:", department);
        
        // Get all providers for the department
        const provs = await athenaService.getProviders(parseInt(deptId));
        
        // If department has a providerlist, filter providers to only include those in the list
        if (department && department.providerlist && department.providerlist.length > 0) {
          const filteredProvs = provs.filter(p => 
            department.providerlist.includes(p.providerid.toString())
          );
          console.log('Filtered providers for department:', filteredProvs);
          setProviders(filteredProvs || []);
        } else {
          // If no providerlist is available, use all providers
          console.log('No providerlist for department, using all providers');
          setProviders(provs || []);
        }
      } else {
        setProviders([]);
      }
      setSelectedProvider('');
    } catch (error) {
      console.error("Error loading providers for department:", error);
      setProviders([]);
    }
    
    // Fetch appointments for the new department
    fetchAppointments();
  };

  const handleProviderChange = (event) => {
    setSelectedProvider(event.target.value);
  };

  const handleViewAppointmentDetails = (appointment) => {
    // Get the original, complete appointment data
    const rawIndex = appointmentMap[appointment.id];
    const rawAppointment = rawAppointments[rawIndex];
    
    // Pass the complete appointment data with all details
    setSelectedAppointment(rawAppointment);
  };

  const fetchCancelReasons = async () => {
    setLoadingReasons(true);
    try {
      const response = await athenaService.getAppointmentCancelReasons();
      console.log('Cancel reasons:', response);
      
      // Check if the response has appointmentcancelreasons array property
      if (response && response.appointmentcancelreasons && Array.isArray(response.appointmentcancelreasons)) {
        const reasonsArray = response.appointmentcancelreasons;
        setCancelReasons(reasonsArray);
        
        if (reasonsArray.length > 0) {
          // Use appointmentcancelreasonid instead of id
          setSelectedCancelReasonId(reasonsArray[0].appointmentcancelreasonid.toString());
        }
      } else {
        setCancelReasons([]);
      }
    } catch (error) {
      console.error('Error fetching cancel reasons:', error);
      setCancelReasons([]);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not load cancellation reasons';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      // Show detailed error message
      setSnackbar({
        open: true,
        message: `Failed to load cancellation reasons: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setLoadingReasons(false);
    }
  };

  const handleOpenCancelDialog = (appointment) => {
    setAppointmentToCancel(appointment);
    setCancelDialogOpen(true);
    fetchCancelReasons();
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setAppointmentToCancel(null);
    setSelectedCancelReasonId('');
    setCustomCancelReason('');
  };

  const handleCancelReasonChange = (event) => {
    setSelectedCancelReasonId(event.target.value);
  };

  const handleCustomReasonChange = (event) => {
    setCustomCancelReason(event.target.value);
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel) return;
    
    // Enable loading state and disable button
    setConfirmCancelLoading(true);
    
    try {
      const selectedReason = cancelReasons.find(reason => reason.appointmentcancelreasonid.toString() === selectedCancelReasonId);
      
      const response = await athenaService.cancelAppointment({
        appointmentId: appointmentToCancel.id,
        patientId: appointmentToCancel.patient?.id,
        appointmentCancelReasonId: parseInt(selectedCancelReasonId, 10),
        cancellationReason: customCancelReason || (selectedReason ? selectedReason.name : 'Cancelled by user'),
        ignoreSchedulablePermission: true
      });
      
      console.log('Appointment cancelled:', response);
      
      setSnackbar({
        open: true,
        message: 'Appointment cancelled successfully',
        severity: 'success'
      });
      
      // Close dialog and refresh appointments
      handleCloseCancelDialog();
      fetchAppointments();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'Failed to cancel appointment';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      // Show detailed error message
      setSnackbar({
        open: true,
        message: `Failed to cancel appointment: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setConfirmCancelLoading(false);
      handleCloseCancelDialog();
    }
  };

  const handleOpenRescheduleDialog = (appointment) => {
    console.log('Opening reschedule dialog with appointment:', appointment);
    setAppointmentToReschedule(appointment);
    setRescheduleDialogOpen(true);
    setSelectedRescheduleDate(new Date());
    
    // Reset states to ensure clean start
    setSelectedSlotId('');
    setRescheduleReason('');
    setAvailableSlots([]);
    
    // Location and provider will be set by useEffect when departmentsByLocation is ready
    if (!appointment?.location) {
      console.log('No location found in appointment');
    }
    
    fetchCancelReasons(); // Reuse cancel reasons for rescheduling
  };
  
  // Fallback method to find department by name
  const findDepartmentByNameAndSetup = (appointment) => {
    if (!appointment || !appointment.location) return;
    
    // Check if the appointment location contains brackets with city
    let departmentName = appointment.location;
    let departmentCity = null;
    
    // Extract department name and city if in format "Name [City]"
    const bracketMatch = appointment.location.match(/(.*)\s*\[(.*)\]/);
    if (bracketMatch) {
      departmentName = bracketMatch[1].trim();
      departmentCity = bracketMatch[2].trim();
    }
    
    // Find the department based on name (and city if available)
    let departmentObj = null;
    if (departmentCity) {
      departmentObj = departments.find(dept => 
        dept.name === departmentName && dept.city === departmentCity
      );
    } else {
      departmentObj = departments.find(dept => dept.name === departmentName);
    }
    
    if (departmentObj) {
      const deptId = departmentObj.departmentid?.toString() || departmentObj.id?.toString();
      console.log('Found matching department:', departmentObj);
      console.log('Setting department to:', deptId);
      
      setSelectedRescheduleDepartment(deptId);
      
      // Fetch providers for this department
      fetchProvidersForReschedule(deptId);
      
      // Pre-select the provider if available
      if (appointment.providerid) {
        setSelectedRescheduleProvider(appointment.providerid.toString());
      }
    }
  };

  const handleCloseRescheduleDialog = () => {
    setRescheduleDialogOpen(false);
    setHasInitializedReschedule(false);
    setAppointmentToReschedule(null);
    setSelectedSlotId('');
    setRescheduleReason('');
    setSelectedLocation('');
    setSelectedRescheduleDepartment('');
    setSelectedRescheduleProvider('');
    setAvailableSlots([]);
  };

  const fetchProvidersForReschedule = async (departmentId) => {
    console.log('Fetching providers for reschedule, department:', departmentId);
    setLoadingRescheduleProviders(true);
    try {
      // Find the selected department to get its providerlist
      const department = departments.find(d => 
        d.departmentid === departmentId || 
        d.departmentid === parseInt(departmentId)
      );
      
      // Get all providers for the department
      const provs = await athenaService.getProviders(parseInt(departmentId));
      
      // If department has a providerlist, filter providers to only include those in the list
      if (department && department.providerlist && department.providerlist.length > 0) {
        const filteredProvs = provs.filter(p => 
          department.providerlist.includes(p.providerid.toString())
        );
        console.log('Reschedule - Filtered providers for department:', filteredProvs);
        setRescheduleProviders(filteredProvs || []);
      } else {
        // If no providerlist is available, use all providers
        console.log('Reschedule - No providerlist for department, using all providers');
        setRescheduleProviders(provs || []);
      }
    } catch (error) {
      console.error('Error fetching providers for reschedule:', error);
      setRescheduleProviders([]);
    } finally {
      setLoadingRescheduleProviders(false);
    }
  };

  const handleRescheduleDepartmentChange = (event) => {
    const deptId = event.target.value;
    console.log('Department changed to:', deptId);
    setSelectedRescheduleDepartment(deptId);
    setSelectedRescheduleProvider('');
    
    if (deptId) {
      fetchProvidersForReschedule(deptId);
    } else {
      setRescheduleProviders([]);
    }
  };

  // Handle location change for reschedule
  const handleRescheduleLocationChange = async (event) => {
    const location = event.target.value;
    setSelectedLocation(location);
    setSelectedRescheduleProvider('');
    
    if (!location) return;
    
    try {
      setLoadingRescheduleProviders(true);
      
      // Get all departments for this location
      const locationDepartments = departmentsByLocation[location] || [];
      console.log(`Found ${locationDepartments.length} departments for location ${location}`);
      
      if (locationDepartments.length === 0) {
        setRescheduleProviders([]);
        setLoadingRescheduleProviders(false);
        return;
      }
      
      // Select the first department from this location
      const firstDepartment = locationDepartments[0];
      setSelectedRescheduleDepartment(firstDepartment.departmentid.toString());
      
      // To combine all providers across departments
      const allProviders = [];
      // Set to track unique provider IDs
      const allProviderIds = new Set();
      
      // Loop through all departments in this location
      for (const dept of locationDepartments) {
        // Load providers for each department
        const departmentProviders = await athenaService.getProviders(parseInt(dept.departmentid));
        
        // Filter and add to all providers collection
        for (const provider of departmentProviders) {
          // Only add if not already in the collection
          if (!allProviderIds.has(provider.providerid)) {
            allProviderIds.add(provider.providerid);
            allProviders.push(provider);
          }
        }
      }
      
      console.log(`Combined ${allProviders.length} providers across all departments in ${location}`);
      setRescheduleProviders(allProviders);
      
      // Auto-select provider if this is the original appointment's provider
      if (appointmentToReschedule && appointmentToReschedule.providerid) {
        const providerExists = allProviders.some(p => p.providerid.toString() === appointmentToReschedule.providerid.toString());
        if (providerExists) {
          setSelectedRescheduleProvider(appointmentToReschedule.providerid.toString());
        }
      }
      
    } catch (error) {
      console.error('Error loading providers for location:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not load providers';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: `Failed to load providers: ${errorMessage}`,
        severity: 'error'
      });
      setRescheduleProviders([]);
    } finally {
      setLoadingRescheduleProviders(false);
    }
  };

  const handleRescheduleProviderChange = (event) => {
    setSelectedRescheduleProvider(event.target.value);
  };

  const handleRescheduleDateChange = (newDate) => {
    if (isValid(newDate)) {
      setSelectedRescheduleDate(newDate);
    }
  };

  const fetchAvailableSlots = async () => {
    if (!selectedRescheduleDepartment) {
      setSnackbar({
        open: true,
        message: 'Please select a department first',
        severity: 'warning'
      });
      return;
    }
    
    if (!appointmentToReschedule) {
      return;
    }
    
    setLoadingSlots(true);
    try {
      // Format date for API
      const formattedDate = format(selectedRescheduleDate, 'MM/dd/yyyy');
      const endDateFormatted = format(addDays(selectedRescheduleDate, 1), 'MM/dd/yyyy');
      
      // Log all appointment properties for debugging
      console.log('Appointment object:', appointmentToReschedule);
      
      // Ensure all parameters are properly typed for the API call
      const departmentId = selectedRescheduleDepartment ? parseInt(selectedRescheduleDepartment, 10) : null;
      const providerId = selectedRescheduleProvider ? parseInt(selectedRescheduleProvider, 10) : null;
      
      // Get the original appointment data from the raw appointments
      const rawIndex = appointmentMap[appointmentToReschedule.id];
      const rawAppointment = rawAppointments[rawIndex];
      
      console.log('Raw appointment data:', rawAppointment);
      
      // Extract the appointment type ID from the raw appointment data
      // Athena's API structure can vary, so check all possible locations
      let appointmentTypeId = 1; // Default fallback
      let appointmentTypeName = 'OFFICE VISIT'; // Default name
      
      // Handle appointment type based on what we're seeing in the logs
      if (rawAppointment) {
        // Based on the actual data structure we're seeing, we need to handle string appointment types
        // Option 1: Direct appointmenttypeid property (best case)
        if (rawAppointment.appointmenttypeid) {
          appointmentTypeId = parseInt(rawAppointment.appointmenttypeid, 10);
          appointmentTypeName = rawAppointment.appointmenttype || 'OFFICE VISIT';
          console.log(`Found appointment type ID ${appointmentTypeId} from raw appointment data`);
        }
        // Option 2: Find appointment type in standard mappings based on name
        else if (rawAppointment.appointmenttype && typeof rawAppointment.appointmenttype === 'string') {
          // Map common appointment types to IDs - this matches AthenaHealth's system
          const typeMapping = {
            'OFFICE VISIT': 1,
            'NEW PATIENT 20': 2,
            'NEW PATIENT 40': 3,
            'FOLLOW UP': 4,
            'TELEHEALTH': 5,
            // Add more mappings as needed
          };
          
          const typeKey = Object.keys(typeMapping).find(key => 
            rawAppointment.appointmenttype.toUpperCase().includes(key));
          
          if (typeKey) {
            appointmentTypeId = typeMapping[typeKey];
            appointmentTypeName = rawAppointment.appointmenttype;
            console.log(`Mapped appointment type "${rawAppointment.appointmenttype}" to ID ${appointmentTypeId}`);
          } else {
            // If we can't map it, use a safe default
            appointmentTypeId = 1; // OFFICE VISIT is usually safe
            appointmentTypeName = rawAppointment.appointmenttype;
            console.log(`Could not map "${rawAppointment.appointmenttype}" to a known ID, using default: ${appointmentTypeId}`);
          }
        }
        // Option 3: Use appointmentid as a last resort
        else if (rawAppointment.appointmentid) {
          appointmentTypeId = 1; // Default to OFFICE VISIT
          appointmentTypeName = 'OFFICE VISIT';
          console.log('No appointment type found, using default OFFICE VISIT (1)');
        } else {
          appointmentTypeId = 1; // Default fallback
          appointmentTypeName = 'OFFICE VISIT';
          console.log('No identifiable appointment type, using default');
        }
      } else {
        console.log('No raw appointment data found, using default appointment type ID');
        appointmentTypeId = 1;
        appointmentTypeName = 'OFFICE VISIT';
      }
      
      const appointmentType = {
        appointmenttypeid: appointmentTypeId,
        name: appointmentTypeName
      };
      
      console.log('Constructed appointmentType object:', appointmentType);
      
      console.log('Fetching slots with:', {
        departmentId,
        appointmentType,
        providerId,
        startDate: formattedDate,
        endDate: endDateFormatted
      });
      
      // Fetch available appointment slots
      const slots = await athenaService.getAvailableSlots(
        departmentId,
        appointmentType, // Pass as object with {appointmenttypeid, name} structure
        providerId,
        formattedDate,
        endDateFormatted
      );
      
      console.log('Available slots:', slots);
      setAvailableSlots(slots || []);
      
      // Select first slot by default if available
      if (slots && slots.length > 0) {
        setSelectedSlotId(slots[0].appointmentid || slots[0].id);
      } else {
        setSelectedSlotId('');
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not load available appointment slots';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      // Show detailed error message in snackbar
      setSnackbar({
        open: true,
        message: `Failed to load available appointment slots: ${errorMessage}`,
        severity: 'error'
      });
      
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSlotChange = (event) => {
    setSelectedSlotId(event.target.value);
  };

  const handleRescheduleReasonChange = (event) => {
    setRescheduleReason(event.target.value);
  };

  const handleRescheduleAppointment = async () => {
    if (!appointmentToReschedule) return;
    
    if (!selectedSlotId) {
      setSnackbar({
        open: true,
        message: 'Please select an available appointment slot',
        severity: 'warning'
      });
      setLoading(false);
      return;
    }
    
    // Enable loading state and disable button
    setConfirmRescheduleLoading(true);
    
    try {
      // Find the reason text for selected reason ID
      const selectedReason = cancelReasons.find(reason => reason.appointmentcancelreasonid.toString() === selectedCancelReasonId);
      
      const response = await athenaService.rescheduleAppointment({
        appointmentId: appointmentToReschedule.id,
        newAppointmentId: selectedSlotId,
        patientId: appointmentToReschedule.patient?.id,
        appointmentCancelReasonId: parseInt(selectedCancelReasonId, 10),
        rescheduleReason: rescheduleReason || 'Patient requested reschedule',
        ignoreschedulablepermission: false
      });
      
      console.log('Appointment rescheduled:', response);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Appointment rescheduled successfully',
        severity: 'success'
      });
      
      // Close the dialog and refresh appointments
      handleCloseRescheduleDialog();
      fetchAppointments();
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'Failed to reschedule appointment';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      // Show detailed error message
      setSnackbar({
        open: true,
        message: `Failed to reschedule appointment: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setConfirmRescheduleLoading(false);
      handleCloseRescheduleDialog();
    }
  };

  const handleCloseAppointmentDetails = () => {
    setSelectedAppointment(null);
    fetchAppointments(); // Add this line to refresh appointments
  };

  // Effect for immediate appointment initialization
  useEffect(() => {
    if (rescheduleDialogOpen && appointmentToReschedule && appointmentToReschedule.id && !hasInitializedReschedule) {
      console.log('Initializing from appointment:', appointmentToReschedule);
      
      // Get the raw appointment data which has the departmentid
      const rawIndex = appointmentMap[appointmentToReschedule.id];
      const rawAppointment = rawAppointments[rawIndex];
      
      console.log('Raw appointment data for location matching:', rawAppointment);
      
      // Mark as initialized to prevent infinite loop
      setHasInitializedReschedule(true);
      
      if (rawAppointment && rawAppointment.departmentid && departments.length > 0) {
        // Find the department object using the departmentid
        const departmentId = rawAppointment.departmentid;
        const departmentObj = departments.find(d => 
          d.departmentid === departmentId || 
          d.departmentid === parseInt(departmentId, 10)
        );
        
        console.log('Found department object:', departmentObj);
        
        if (departmentObj && departmentObj.city && Object.keys(departmentsByLocation).length > 0) {
          // Convert the city name to match the format in the locations dropdown
          // This is similar to how AppointmentDetails does it
          const cityName = departmentObj.city.toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          
          console.log(`Department city name: ${cityName}`);
          
          // Check if this city exists in our departmentsByLocation
          if (departmentsByLocation[cityName]) {
            console.log(`Found matching location: ${cityName}`);
            setSelectedLocation(cityName);
            setSelectedRescheduleDepartment(departmentObj.departmentid.toString());
            
            // Trigger location change handler with a delay to ensure state is updated
            setTimeout(() => {
              handleRescheduleLocationChange({ target: { value: cityName } });
              
              // Set the provider after a slightly longer delay
              setTimeout(() => {
                if (appointmentToReschedule.providerName && rescheduleProviders.length > 0) {
                  console.log('Looking for provider match:', appointmentToReschedule.providerName);
                  const matchingProvider = rescheduleProviders.find(p => 
                    (p.firstname && p.lastname && 
                      `${p.firstname} ${p.lastname}`.toLowerCase() === appointmentToReschedule.providerName.toLowerCase()) ||
                    (p.name && p.name.toLowerCase() === appointmentToReschedule.providerName.toLowerCase()));
                    
                  if (matchingProvider) {
                    const providerId = matchingProvider.providerid || matchingProvider.id;
                    console.log(`Found matching provider: ${providerId}`);
                    setSelectedRescheduleProvider(providerId.toString());
                  }
                }
              }, 500);
            }, 300);
          } else {
            console.log(`City ${cityName} not found in departmentsByLocation:`, Object.keys(departmentsByLocation));
          }
        } else {
          console.log('Department object not found or missing city:', departmentObj);
        }
      } else {
        console.log('No department ID found in raw appointment or departments not loaded yet');
      }
    }
  }, [rescheduleDialogOpen, appointmentToReschedule, departments, departmentsByLocation, rescheduleProviders, appointmentMap, rawAppointments, hasInitializedReschedule]);

  // Effect for handling provider selection after providers are loaded
  useEffect(() => {
    if (rescheduleDialogOpen && appointmentToReschedule?.providerName && rescheduleProviders.length > 0) {
      // Look for the provider by name
      const providerObj = rescheduleProviders.find(
        p => (p.firstname && p.lastname) ? 
          `${p.firstname} ${p.lastname}` === appointmentToReschedule.providerName :
          p.name === appointmentToReschedule.providerName
      );
      
      if (providerObj) {
        const provId = providerObj.providerid?.toString() || providerObj.id?.toString();
        console.log('Found matching provider:', providerObj);
        console.log('Setting provider to:', provId);
        setSelectedRescheduleProvider(provId);
      } else {
        console.log('No matching provider found for:', appointmentToReschedule.providerName);
      }
    }
  }, [rescheduleProviders, appointmentToReschedule, rescheduleDialogOpen]);

  const handleMarkAsNoShow = async (appointment) => {
    try {
      setLoading(true)
      // Use the fixed PATIENT NO SHOW reason ID (9)
      const noShowReasonId = 9;
      
      await athenaService.cancelAppointment({
        appointmentId: appointment.id,
        patientId: appointment.patient?.id,
        appointmentCancelReasonId: noShowReasonId,
        cancellationReason: 'PATIENT NO SHOW',
        ignoreSchedulablePermission: true
      });
      
      console.log('Appointment marked as no-show:', appointment.id);
      
      setSnackbar({
        open: true,
        message: 'Appointment marked as no-show successfully',
        severity: 'success'
      });
      
      // Refresh appointments
      fetchAppointments();
    } catch (error) {
      console.error('Error marking appointment as no-show:', error);
      
      setSnackbar({
        open: true,
        message: `Failed to mark appointment as no-show: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Function to organize departments by location (city)
  const organizeDepartmentsByLocation = (departmentsArray) => {
    const locationMap = {};
    
    departmentsArray.forEach(dept => {
      if (dept.city && dept.providerlist && dept.providerlist.length > 0) {
        const locationName = dept.city;
        if (!locationMap[locationName]) {
          locationMap[locationName] = [];
        }
        locationMap[locationName].push(dept);
      }
    });
    
    return locationMap;
  };

  const fetchDepartments = async () => {
    try {
      const response = await athenaService.getDepartments();
      console.log('Departments:', response);
      setDepartments(response);
      
      // Organize departments by location
      const departmentsByLoc = organizeDepartmentsByLocation(response);
      setDepartmentsByLocation(departmentsByLoc);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load departments',
        severity: 'error'
      });
    }
  };

  // We no longer use a full-page loading indicator
  // Instead, we'll show loading states in specific parts of the UI

  if (selectedAppointment) {
    return (
      <AppointmentDetails 
        appointment={selectedAppointment} 
        onClose={handleCloseAppointmentDetails} 
        initialDepartments={departments}
        initialProviders={providers}
      />
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', pb: 5, position: 'relative' }}>
      {/* Loading overlay that doesn't hide the UI */}
      {loading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 1000,
        }}>
          <CircularProgress sx={{ color: theme.palette.primary.main, mb: 2 }} />
          <Typography variant="body2" color="textSecondary">Loading appointments...</Typography>
        </Box>
      )}
      {/* Header */}
      <Box 
        sx={{ 
          p: 2, 
          bgcolor: 'background.paper', 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <ClockIcon sx={{ mr: 1 }} />
          Appointments
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: 'white' }}></div>
            ) : <RefreshIcon />}
            onClick={fetchAppointments}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleBookAppointment}
          >
            Book Appointment
          </Button>
        </Box>
      </Box>
      
      {/* Filter Section */}
      <Paper elevation={0} sx={{ 
        p: 2, 
        bgcolor: 'background.paper', 
        borderRadius: 1,
        borderBottom: 1, 
        borderColor: 'divider',
        mb: 3,
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          maxWidth: '800px',
          width: '100%'
        }}>
          <FormControl fullWidth sx={{ maxWidth: { xs: '100%', md: 200 } }}>
            <InputLabel 
              id="location-select-label"
              shrink={true}
            >
              Location
            </InputLabel>
            <Select
              labelId="location-select-label"
              value={selectedLocation}
              onChange={handleLocationChange}
              label="Location"
              size="small"
              displayEmpty
              renderValue={(value) => value === "" ? "Default Location" : value}
            >
              <MenuItem value="">Default Location</MenuItem>
              {Object.keys(departmentsByLocation).map((locationName) => (
                <MenuItem key={locationName} value={locationName}>
                  {locationName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Department field - hidden but still functional */}
          <FormControl fullWidth sx={{ maxWidth: { xs: '100%', md: 200 }, display: 'none' }}>
            <InputLabel 
              id="department-label"
              shrink={true}
            >
              Department
            </InputLabel>
            <Select
              labelId="department-label"
              value={selectedDepartment}
              onChange={handleDepartmentChange}
              label="Department"
              size="small"
              disabled={!selectedLocation}
              displayEmpty
              renderValue={(value) => {
                if (value === '1') return "All Departments";
                const dept = departments.find(d => d.departmentid.toString() === value.toString());
                return dept ? `${dept.name}` : "Department";
              }}
            >
              <MenuItem value="1">All Departments</MenuItem>
              {selectedLocation && departmentsByLocation[selectedLocation] ? 
                departmentsByLocation[selectedLocation].map((dept) => (
                  <MenuItem key={dept.departmentid} value={dept.departmentid.toString()}>
                    {dept.name}
                  </MenuItem>
                )) : 
                departments.map(dept => (
                  dept.departmentid !== 1 && (
                    <MenuItem key={dept.departmentid} value={dept.departmentid.toString()}>
                      {dept.name} [{dept.city}]
                    </MenuItem>
                  )
                ))
              }
            </Select>
          </FormControl>
          
          <FormControl fullWidth sx={{ maxWidth: { xs: '100%', md: 200 } }}>
            <InputLabel 
              id="provider-label"
              shrink={true}
            >
              Provider
            </InputLabel>
            <Select
              labelId="provider-label"
              value={selectedProvider}
              onChange={handleProviderChange}        
              label="Provider"
              size="small"
              disabled={!selectedLocation}
              displayEmpty
              renderValue={(value) => {
                if (value === '') return "All Providers";
                const provider = providers.find(p => p.providerid.toString() === value.toString());
                return provider ? formatProviderName(provider) : "Providers";
              }}
            >
              <MenuItem value="">All Providers</MenuItem>
              {providers.map(provider => (
                <MenuItem key={provider.providerid} value={provider.providerid.toString()}>
                  {formatProviderName(provider)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Patient Search Field */}
          <TextField
            label="Search Patient"
            variant="outlined"
            size="small"
            fullWidth
            value={patientSearch}
            onChange={(e) => setPatientSearch(e.target.value)}
            placeholder="Name or ID"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: patientSearch ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setPatientSearch('')}
                    edge="end"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ maxWidth: { xs: '100%', md: 200 } }}
          />
          
          <Button 
            variant="contained" 
            sx={{ 
              minWidth: '120px',
              height: '40px',
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              }
            }}
            onClick={fetchAppointments}
          >
            Apply Filters
          </Button>
        </Box>
      </Paper>
      
      {/* Appointment Cards */}
      <Box sx={{ mt: 3, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {filteredAppointments && filteredAppointments.length > 0 ? (
          filteredAppointments
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((appointment) => (
            <Paper
              key={appointment.id}
              elevation={0}
              sx={{
                width: '85%', // Narrower width to match reference image
                maxWidth: '900px',
                mb: 2,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper,
                overflow: 'hidden'
              }}
            >
              <Box sx={{ p: 2.5 }}>
                {/* Date and Time */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarIcon fontSize="small" sx={{ color: theme.palette.text.secondary, mr: 1 }} />
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {appointment.date} at {appointment.time}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: appointment.status === 'Scheduled' ? '#4CAF50' : 
                                appointment.status === 'Cancelled' ? '#F44336' : 
                                appointment.status === 'No Show' ? '#F44336' : 
                                appointment.status === 'Completed' ? '#9C27B0' : '#2196F3',
                        mr: 1
                      }}
                    />
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                      {appointment.status}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Patient Name and Tag */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                    {appointment.patient.name}
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      bgcolor: appointment.patient.isNew ? 'rgba(76, 175, 80, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                      color: appointment.patient.isNew ? '#4CAF50' : '#1976D2',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      fontSize: '0.675rem',
                      fontWeight: 600,
                      ml: 1
                    }}
                  >
                    {appointment.patient.isNew ? 'NEW PATIENT' : 'RETURNING PATIENT'}
                  </Box>
                </Box>
                
                <Grid container spacing={1}>
                  {/* Left Column */}
                  <Grid item xs={6}>
                    {/* Patient Email */}
                    {appointment.patient.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: appointment.patient.phone ? 0.5 : 0 }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {appointment.patient.email}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Patient Phone */}
                    {appointment.patient.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {appointment.patient.phone}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* New Patient Badge - when no contact info available */}
                    {!appointment.patient.email && !appointment.patient.phone && appointment.patient.isNew && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          New Patient
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  
                  {/* Right Column */}
                  <Grid item xs={6}>
                    {/* Provider */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                      <PersonIcon fontSize="small" sx={{ color: theme.palette.text.secondary, mr: 1, mt: 0.25 }} />
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {appointment.providerName}
                      </Typography>
                    </Box>
                    
                    {/* Location */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <LocationIcon fontSize="small" sx={{ color: theme.palette.text.secondary, mr: 1, mt: 0.25 }} />
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {appointment.location}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Date Information - Received/Requested Date */}
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid item xs={12}>
                    {appointment.receivedDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <AccessTimeIcon fontSize="small" sx={{ color: theme.palette.text.secondary, mr: 1 }} />
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          Received {appointment.receivedDate} at {appointment.time}
                        </Typography>
                      </Box>
                    )}
                    {appointment.requestedDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <AccessTimeIcon fontSize="small" sx={{ color: theme.palette.text.secondary, mr: 1 }} />
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          Requested {appointment.requestedDate} at {appointment.time}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <BusinessIcon fontSize="small" sx={{ color: theme.palette.text.secondary, mr: 1 }} />
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {appointment.platform}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Reason - Only shown if specified */}
                {appointment.reason !== 'No reason specified' && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
                    <SubjectIcon fontSize="small" sx={{ color: theme.palette.text.secondary, mr: 1, mt: 0.25 }} />
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      {appointment.reason}
                    </Typography>
                  </Box>
                )}
                
                {/* "No reason specified" for appointments without reasons */}
                {appointment.reason === 'No reason specified' && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
                    <SubjectIcon fontSize="small" sx={{ color: theme.palette.text.secondary, mr: 1, mt: 0.25 }} />
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      No reason specified
                    </Typography>
                  </Box>
                )}
                
                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  <Tooltip title="Reschedule">
                    <IconButton 
                      size="small" 
                      sx={{ color: theme.palette.text.secondary }}
                      onClick={() => handleOpenRescheduleDialog(appointment)}
                    >
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* <Tooltip title="Edit">
                    <IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip> */}
                  <Tooltip title="Cancel">
                    <IconButton 
                      size="small" 
                      sx={{ color: theme.palette.text.secondary }}
                      onClick={() => handleOpenCancelDialog(appointment)}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Message">
                    <IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
                      <MessageIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Mark as No-Show">
                    <IconButton 
                      size="small" 
                      sx={{ color: theme.palette.text.secondary }}
                      onClick={() => handleMarkAsNoShow(appointment)}
                    >
                      <NoShowIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View details">
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        borderRadius: 1,
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        py: 0.5,
                        height: 24,
                        minWidth: 'auto',
                        borderColor: theme.palette.divider,
                        color: theme.palette.text.primary
                      }}
                      onClick={() => handleViewAppointmentDetails(appointment)}
                    >
                      Details
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          ))
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '200px',
            width: '100%'
          }}>
            <CalendarIcon sx={{ fontSize: 50, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No appointments found
            </Typography>
            <Typography variant="body2" color="text.disabled" align="center" sx={{ mt: 1, maxWidth: 400 }}>
              Try adjusting your filters or selecting different options to see more appointments
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Pagination */}
      <Box sx={{ 
        p: 2, 
        mb: 3,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        borderTop: `1px solid ${theme.palette.divider}`,
        width: '85%',
        maxWidth: '900px',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {totalAppointments > 0 ? (
              `Showing ${Math.min(page * rowsPerPage + 1, totalAppointments)} to ${Math.min(page * rowsPerPage + rowsPerPage, totalAppointments)} of ${totalAppointments} appointments`
            ) : (
              'No appointments to display'
            )}
          </Typography>
          {isLoadingMore && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Loading more... ({loadedCount})
              </Typography>
            </Box>
          )}
        </Box>
        {totalAppointments > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              displayEmpty
              size="small"
              sx={{ height: 32, mr: 1, minWidth: 80 }}
            >
              <MenuItem value={5}>5 rows</MenuItem>
              <MenuItem value={10}>10 rows</MenuItem>
              <MenuItem value={25}>25 rows</MenuItem>
            </Select>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              sx={{ mr: 1, minWidth: 90 }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredAppointments.length / rowsPerPage) - 1}
              sx={{ minWidth: 90 }}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>
      
      {/* Appointment Form Dialog */}
      <AppointmentForm 
        open={openAppointmentForm}
        onClose={handleCloseAppointmentForm}
        onSave={handleSaveAppointment}
      />
      
      {/* Appointment Cancellation Dialog */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={handleCloseCancelDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          {loadingReasons ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please select a reason for cancelling this appointment:
              </Typography>
              
              {cancelReasons.length > 0 ? (
                <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                  <FormLabel component="legend">Cancellation Reason</FormLabel>
                  <RadioGroup
                    value={selectedCancelReasonId}
                    onChange={handleCancelReasonChange}
                  >
                    {cancelReasons.map((reason) => (
                      <FormControlLabel 
                        key={reason.appointmentcancelreasonid} 
                        value={reason.appointmentcancelreasonid.toString()} 
                        control={<Radio />} 
                        label={reason.name} 
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              ) : (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  No cancellation reasons available. Please enter a custom reason.
                </Typography>
              )}
              
              <TextField
                label="Additional Comments (Optional)"
                multiline
                rows={3}
                fullWidth
                value={customCancelReason}
                onChange={handleCustomReasonChange}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} disabled={confirmCancelLoading}>Cancel</Button>
          <Button 
            onClick={handleCancelAppointment} 
            color="primary" 
            variant="contained"
            disabled={((!selectedCancelReasonId && cancelReasons.length > 0) || confirmCancelLoading)}
            startIcon={confirmCancelLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {confirmCancelLoading ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Reschedule Dialog */}
      <Dialog 
        open={rescheduleDialogOpen} 
        onClose={handleCloseRescheduleDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Step 1: Select Location */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
                Step 1: Select Location and Provider
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }} disabled={loadingDepartments}>
                <InputLabel id="reschedule-location-label">Location</InputLabel>
                <Select
                  labelId="reschedule-location-label"
                  id="reschedule-location-select"
                  value={selectedLocation}
                  label="Location"
                  onChange={handleRescheduleLocationChange}
                >
                  <MenuItem value="">
                    <em>Select location</em>
                  </MenuItem>
                  {Object.keys(departmentsByLocation).map((locationName) => (
                    <MenuItem key={locationName} value={locationName}>
                      {locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Hidden department select - populated automatically based on location */}
              <input 
                type="hidden" 
                value={selectedRescheduleDepartment} 
                onChange={() => {}}
              />
              
              <FormControl fullWidth sx={{ mb: 2 }} disabled={loadingRescheduleProviders || !selectedRescheduleDepartment || (appointmentToReschedule && appointmentToReschedule.providerid && selectedRescheduleProvider === appointmentToReschedule.providerid.toString())}>
                <InputLabel id="reschedule-provider-label">
                  Provider {appointmentToReschedule && appointmentToReschedule.providerid && selectedRescheduleProvider === appointmentToReschedule.providerid.toString() ? '(Auto-filled)' : '(Optional)'}
                </InputLabel>
                <Select
                  labelId="reschedule-provider-label"
                  id="reschedule-provider-select"
                  value={selectedRescheduleProvider}
                  label={`Provider ${appointmentToReschedule && appointmentToReschedule.providerid && selectedRescheduleProvider === appointmentToReschedule.providerid.toString() ? '(Auto-filled)' : '(Optional)'}`}
                  onChange={handleRescheduleProviderChange}
                  inputProps={{
                    readOnly: appointmentToReschedule && appointmentToReschedule.providerid && selectedRescheduleProvider === appointmentToReschedule.providerid.toString()
                  }}
                >
                  <MenuItem value="">
                    <em>All Providers</em>
                  </MenuItem>
                  {rescheduleProviders.map((provider) => (
                    <MenuItem key={provider.providerid || provider.id} value={provider.providerid || provider.id}>
                      {provider.firstname ? `${provider.firstname} ${provider.lastname}` : provider.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Step 2: Select Date */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
                Step 2: Select Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Appointment Date"
                  value={selectedRescheduleDate}
                  onChange={handleRescheduleDateChange}
                  minDate={new Date()}
                  slotProps={{ textField: { fullWidth: true } }}
                  sx={{ mb: 2 }}
                />
              </LocalizationProvider>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={fetchAvailableSlots}
                  disabled={!selectedRescheduleDepartment || !isValid(selectedRescheduleDate)}
                >
                  Find Available Slots
                </Button>
              </Box>
            </Grid>
            
            {/* Step 3: Select Time Slot */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
                Step 3: Select Available Time
              </Typography>
              
              {loadingSlots ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {availableSlots.length > 0 ? (
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel id="slot-select-label">Available Appointment Slots</InputLabel>
                      <Select
                        labelId="slot-select-label"
                        id="slot-select"
                        value={selectedSlotId}
                        label="Available Appointment Slots"
                        onChange={handleSlotChange}
                      >
                        {availableSlots.map((slot) => (
                          <MenuItem 
                            key={slot.appointmentid || slot.id} 
                            value={slot.appointmentid || slot.id}
                          >
                            {slot.date || slot.appointmentdate}, {slot.starttime || slot.appointmenttime} - {slot.duration ? `${slot.duration} min` : 'Duration not specified'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body2" color="error" sx={{ mb: 3 }}>
                      {availableSlots.length === 0 && selectedRescheduleDepartment ? 
                        'No available appointment slots found for the selected date. Please try another date.' :
                        'Search for available slots by selecting a location and date.'}
                    </Typography>
                  )}
                </>
              )}
            </Grid>
            
            {/* Step 4: Reason for Rescheduling */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
                Step 4: Reason for Rescheduling
              </Typography>
              
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <FormLabel component="legend">Reschedule Reason</FormLabel>
                <RadioGroup
                  value={selectedCancelReasonId}
                  onChange={handleCancelReasonChange}
                >
                  {cancelReasons.map((reason) => (
                    <FormControlLabel 
                      key={reason.appointmentcancelreasonid} 
                      value={reason.appointmentcancelreasonid.toString()} 
                      control={<Radio />} 
                      label={reason.name} 
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              
              <TextField
                label="Additional Comments (Optional)"
                multiline
                rows={3}
                fullWidth
                value={rescheduleReason}
                onChange={handleRescheduleReasonChange}
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRescheduleDialog} disabled={confirmRescheduleLoading}>Cancel</Button>
          <Button 
            onClick={handleRescheduleAppointment} 
            color="primary" 
            variant="contained"
            disabled={!selectedSlotId || (!selectedCancelReasonId && cancelReasons.length > 0) || confirmRescheduleLoading}
            startIcon={confirmRescheduleLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {confirmRescheduleLoading ? 'Rescheduling...' : 'Confirm Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity || 'info'}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Create Calendar component with real data
const CalendarView = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [weekStart, setWeekStart] = useState(new Date()); // Start with current week
  const [selectedFilter, setSelectedFilter] = useState('booked');
  const [departments, setDepartments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('1');
  const [selectedProvider, setSelectedProvider] = useState('4'); // Default to Andreh Carapiet
  
  // Add state for locations (grouped departments)
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('GLENDALE');
  const [departmentsByLocation, setDepartmentsByLocation] = useState({});
  
  // Event cache for instant navigation
  const eventsCacheRef = useRef(new Map()); // key: "YYYY-WW-dept-prov-loc" -> events[]
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Helper to generate cache key for a week
  const getWeekKey = useCallback((date, dept, prov, loc) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const startOfYear = new Date(year, 0, 1);
    const weekNum = Math.ceil(((d - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
    return `${year}-W${weekNum}-${dept || 'all'}-${prov || 'all'}-${loc || 'all'}`;
  }, []);

  const timeSlots = [
    "8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM",
    "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM",
    "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
    "5:00 PM", "5:30 PM"
  ];

  // Initialize week to start from today
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setWeekStart(today); // Start from today, not Monday
    
    // Load departments and providers
    loadDepartmentsAndProviders();
  }, []);
  
  // Generate days for the week
  const generateWeekDays = (startDate) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };
  
  const weekDays = generateWeekDays(weekStart);
  
  // Function to load departments and providers
  const loadDepartmentsAndProviders = async () => {
    try {
      // Load departments
      const depts = await athenaService.getDepartments();
      
      // Filter out departments with empty providerlist
      const filteredDepts = depts.filter(dept => 
        dept.providerlist && dept.providerlist.length > 0
      );
      
      console.log('Calendar: Departments with providers:', filteredDepts);
      setDepartments(filteredDepts || []);
      
      // Group departments by location (city)
      const deptsByLocation = {};
      const locationList = [];
      
      filteredDepts.forEach(dept => {
        const locationName = dept.city || 'Unknown';
        
        // Initialize location group if it doesn't exist
        if (!deptsByLocation[locationName]) {
          deptsByLocation[locationName] = [];
          locationList.push({
            name: locationName,
            departments: []
          });
        }
        
        // Add department to its location group
        deptsByLocation[locationName].push(dept);
        
        // Update the location list with this department
        const locationIndex = locationList.findIndex(loc => loc.name === locationName);
        if (locationIndex >= 0) {
          locationList[locationIndex].departments.push(dept);
        }
      });
      
      console.log("Calendar: Departments grouped by location:", deptsByLocation);
      console.log("Calendar: Location list:", locationList);
      
      setLocations(locationList);
      setDepartmentsByLocation(deptsByLocation);
      
      // Load providers for the default department
      if (selectedDepartment) {
        const provs = await athenaService.getProviders(parseInt(selectedDepartment));
        setProviders(provs || []);
      }
    } catch (error) {
      console.error("Error loading departments or providers:", error);
    }
  };
  
  // Handle location change
  const handleLocationChange = async (event) => {
    setLoading(true);
    const location = event.target.value;
    console.log("Calendar: Selected location:", location);
    
    // Clear cache and events immediately when filter changes
    eventsCacheRef.current.clear();
    setEvents([]); // Clear stale data immediately
    setLoading(true); // Show loading state
    console.log('[Calendar] Cache cleared on location change');
    
    setSelectedLocation(location);
    setSelectedDepartment(''); // Empty means 'All Departments'
    setSelectedProvider(''); // Reset provider
    console.log("Calendar: Departments by location:", departmentsByLocation);
    
    // If location is selected, get all providers for departments in this location
    if (location && departmentsByLocation[location]) {
      try {
        // Get all departments for this location
        const locationDepts = departmentsByLocation[location];
        
        // Collect all providers from all departments in this location
        const allLocationProviders = [];
        const providerMap = new Map(); // To track unique providers
        
        // For each department in the location, fetch its providers
        for (const dept of locationDepts) {
          const deptProvs = await athenaService.getProviders(parseInt(dept.departmentid));
          
          // Add unique providers to the collection
          for (const prov of deptProvs) {
            if (!providerMap.has(prov.providerid.toString())) {
              providerMap.set(prov.providerid.toString(), true);
              allLocationProviders.push(prov);
            }
          }
        }
        
        setProviders(allLocationProviders);
      } catch (error) {
        console.error("Error loading providers for location:", error);
        setProviders([]);
      }
    } else {
      // No location selected, reset providers
      setProviders([]);
    }
    
    // Don't set loading=false here - let fetchCalendarEvents manage it
    // The useEffect will trigger fetchCalendarEvents which sets loading appropriately
  };
  
  // Handle department change
  const handleDepartmentChange = async (event) => {
    const deptId = event.target.value;
    
    // Clear cache and events immediately when filter changes
    eventsCacheRef.current.clear();
    setEvents([]); // Clear stale data immediately
    setLoading(true); // Show loading state
    console.log('[Calendar] Cache cleared on department change');
    
    setSelectedDepartment(deptId);
    
    try {
      if (deptId !== '') {
        // Find the selected department to get its providerlist
        const department = departments.find(d => d.departmentid.toString() === deptId.toString());
        
        // Get all providers for the department
        const provs = await athenaService.getProviders(parseInt(deptId));
        
        // If department has a providerlist, filter providers to only include those in the list
        if (department && department.providerlist && department.providerlist.length > 0) {
          const filteredProvs = provs.filter(p => 
            department.providerlist.includes(p.providerid.toString())
          );
          console.log('Filtered providers for department:', filteredProvs);
          setProviders(filteredProvs || []);
        } else {
          // If no providerlist is available, use all providers
          console.log('No providerlist for department, using all providers');
          setProviders(provs || []);
        }
      } else if (selectedLocation && departmentsByLocation[selectedLocation]) {
        // Handle when "All Departments" is selected for a location
        // Get all providers from all departments in this location
        const locationDepts = departmentsByLocation[selectedLocation];
        
        // Collect all providers from all departments in this location
        const allLocationProviders = [];
        const providerMap = new Map(); // To track unique providers
        
        // For each department in the location, fetch its providers
        for (const dept of locationDepts) {
          const deptProvs = await athenaService.getProviders(parseInt(dept.departmentid));
          
          // Add unique providers to the collection
          for (const prov of deptProvs) {
            if (!providerMap.has(prov.providerid.toString())) {
              providerMap.set(prov.providerid.toString(), true);
              allLocationProviders.push(prov);
            }
          }
        }
        
        setProviders(allLocationProviders);
      } else {
        // No department or location selected
        setProviders([]);
      }
      setSelectedProvider('');
    } catch (error) {
      console.error("Error loading providers for department:", error);
      setProviders([]);
    }
    
    // No need to call fetchCalendarEvents() here - the useEffect will handle it
  };
  
  // Handle provider change
  const handleProviderChange = (event) => {
    // Clear cache and events immediately when filter changes
    eventsCacheRef.current.clear();
    setEvents([]); // Clear stale data immediately
    setLoading(true); // Show loading state
    console.log('[Calendar] Cache cleared on provider change');
    
    setSelectedProvider(event.target.value);
  };

  const handleFilterChange = (newFilter) => {
    setSelectedFilter(newFilter);
  };

  const goToPreviousWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() - 7);
    
    // Check cache for instant display
    const weekKey = getWeekKey(newStart, selectedDepartment, selectedProvider, selectedLocation);
    if (eventsCacheRef.current.has(weekKey)) {
      console.log('[Calendar] Using cached events for previous week');
      setEvents(eventsCacheRef.current.get(weekKey));
      setLoading(false);
    }
    
    setWeekStart(newStart);
    // useEffect will fetch if not cached
  };
  
  const goToNextWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + 7);
    
    // Check cache for instant display
    const weekKey = getWeekKey(newStart, selectedDepartment, selectedProvider, selectedLocation);
    if (eventsCacheRef.current.has(weekKey)) {
      console.log('[Calendar] Using cached events for next week');
      setEvents(eventsCacheRef.current.get(weekKey));
      setLoading(false);
    }
    
    setWeekStart(newStart);
    // useEffect will fetch if not cached
  };
  
  const formatDateForAPI = (date) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
  };
  
  const getRandomColor = () => {
    const colors = [
      '#2d5d7c', // Blue
      '#3a9269', // Green
      '#d7742c', // Orange
      '#7c2d5d', // Purple
      '#926f3a', // Brown
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };
  
  // Prefetch adjacent weeks in background for instant navigation
  const prefetchAdjacentWeeks = useCallback(async (currentStart) => {
    setIsLoadingMore(true);
    
    // Prefetch previous week
    const prevWeekStart = new Date(currentStart);
    prevWeekStart.setDate(prevWeekStart.getDate() - 7);
    const prevKey = getWeekKey(prevWeekStart, selectedDepartment, selectedProvider, selectedLocation);
    
    // Prefetch next week
    const nextWeekStart = new Date(currentStart);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);
    const nextKey = getWeekKey(nextWeekStart, selectedDepartment, selectedProvider, selectedLocation);
    
    // Only prefetch if not already cached
    const prefetchPromises = [];
    
    if (!eventsCacheRef.current.has(prevKey)) {
      console.log('[Calendar] Prefetching previous week:', prevKey);
      prefetchPromises.push(fetchWeekData(prevWeekStart, prevKey));
    }
    
    if (!eventsCacheRef.current.has(nextKey)) {
      console.log('[Calendar] Prefetching next week:', nextKey);
      prefetchPromises.push(fetchWeekData(nextWeekStart, nextKey));
    }
    
    if (prefetchPromises.length > 0) {
      await Promise.all(prefetchPromises);
    }
    
    setIsLoadingMore(false);
  }, [selectedDepartment, selectedProvider, selectedLocation, getWeekKey]);
  
  // Helper to fetch a single week's data and cache it
  const fetchWeekData = useCallback(async (weekStartDate, cacheKey) => {
    try {
      const endDate = new Date(weekStartDate);
      endDate.setDate(endDate.getDate() + 7);
      
      let baseOptions = {
        scheduledstartdate: formatDateForAPI(weekStartDate),
        scheduledenddate: formatDateForAPI(endDate),
        showpatientdetail: true
      };
      
      if (selectedProvider) {
        baseOptions.providerId = parseInt(selectedProvider);
      }
      
      let allAppointments = [];
      
      if (selectedDepartment !== '') {
        const options = { ...baseOptions, departmentId: parseInt(selectedDepartment) };
        allAppointments = await athenaService.getBookedAppointments(options) || [];
      } else if (selectedLocation && departmentsByLocation[selectedLocation]) {
        const locationDepts = departmentsByLocation[selectedLocation];
        if (locationDepts?.length > 0) {
          const results = await Promise.all(
            locationDepts.map(dept => 
              athenaService.getBookedAppointments({ ...baseOptions, departmentId: parseInt(dept.departmentid) })
                .catch(() => [])
            )
          );
          allAppointments = results.flat();
        }
      } else {
        allAppointments = await athenaService.getBookedAppointments({ ...baseOptions, departmentId: 1 }) || [];
      }
      
      // Format and cache
      const calendarEvents = allAppointments.map(apt => {
        const apptTime = apt.starttime;
        const [hours, minutes] = apptTime ? apptTime.split(':').map(Number) : [0, 0];
        let apptDate = new Date(apt.date);
        apptDate.setHours(hours, minutes, 0, 0);
        
        const formattedTime = apptDate.toLocaleTimeString('en-US', {
          hour: 'numeric', minute: '2-digit', hour12: true
        });
        
        const patientName = apt.patient 
          ? `${apt.patient.firstname} ${apt.patient.lastname}`
          : 'Unknown Patient';
        
        let providerName = apt.providername || 
          (apt.provider ? `${apt.provider.firstname || ''} ${apt.provider.lastname || ''}`.trim() : null) ||
          `Provider ${apt.providerid || 'Unknown'}`;
        
        return {
          id: apt.appointmentid || `apt-${Math.random().toString(36).substr(2, 9)}`,
          date: apptDate,
          day: apptDate.getDate(),
          hour: apptDate.getHours(),
          minute: apptDate.getMinutes(),
          title: patientName,
          time: formattedTime,
          phone: apt.patient?.mobilephone || apt.patient?.homephone || '',
          type: apt.appointmenttype || "Appointment",
          status: apt.appointmentstatus || "booked",
          duration: apt.duration || null,
          providerName: providerName,
          color: getRandomColor()
        };
      });
      
      eventsCacheRef.current.set(cacheKey, calendarEvents);
      console.log('[Calendar] Prefetched and cached:', cacheKey, calendarEvents.length, 'events');
    } catch (error) {
      console.error('[Calendar] Prefetch error:', error);
    }
  }, [selectedDepartment, selectedProvider, selectedLocation, departmentsByLocation]);
  
  const fetchCalendarEvents = useCallback(async (targetWeekStart = null) => {
    const fetchStart = targetWeekStart || weekStart;
    const weekKey = getWeekKey(fetchStart, selectedDepartment, selectedProvider, selectedLocation);
    
    // Check cache first - return cached data instantly
    if (eventsCacheRef.current.has(weekKey)) {
      console.log('[Calendar] Cache hit for week:', weekKey);
      setEvents(eventsCacheRef.current.get(weekKey));
      setLoading(false);
      return;
    }
    
    console.log('[Calendar] Cache miss, fetching week:', weekKey);
    setLoading(true);
    
    try {
      // Fetch only 7 days for current week (not 60!)
      const endDate = new Date(fetchStart);
      endDate.setDate(endDate.getDate() + 7);
      
      // Base options for all API calls
      let baseOptions = {
        scheduledstartdate: formatDateForAPI(fetchStart),
        scheduledenddate: formatDateForAPI(endDate),
        showpatientdetail: true
      };
      
      // Add provider filter if a specific provider is selected
      if (selectedProvider) {
        baseOptions.providerId = parseInt(selectedProvider);
      }
      
      let allAppointments = [];
      console.log("[Calendar] Fetching 7 days:", baseOptions);
      
      // Handle department selection
      if (selectedDepartment !== '') {
        // Specific department selected - single API call
        const options = { ...baseOptions, departmentId: parseInt(selectedDepartment) };
        const appointments = await athenaService.getBookedAppointments(options);
        allAppointments = appointments || [];
      } else if (selectedLocation && departmentsByLocation[selectedLocation]) {
        // Location selected - PARALLEL fetch for all departments
        const locationDepts = departmentsByLocation[selectedLocation];
        
        if (locationDepts && locationDepts.length > 0) {
          console.log(`[Calendar] Parallel fetch for ${locationDepts.length} departments`);
          
          // Parallel fetch using Promise.all
          const deptPromises = locationDepts.map(dept => {
            const options = { ...baseOptions, departmentId: parseInt(dept.departmentid) };
            return athenaService.getBookedAppointments(options).catch(err => {
              console.error(`[Calendar] Error fetching dept ${dept.name}:`, err);
              return [];
            });
          });
          
          const results = await Promise.all(deptPromises);
          allAppointments = results.flat();
        } else {
          // Fallback if no departments in location
          const options = { ...baseOptions, departmentId: 1 };
          const appointments = await athenaService.getBookedAppointments(options);
          allAppointments = appointments || [];
        }
      } else {
        // No location or department selected, use default
        const options = { ...baseOptions, departmentId: 1 };
        const appointments = await athenaService.getBookedAppointments(options);
        allAppointments = appointments || [];
      }
      
      console.log("Calendar: Combined appointments from all departments:", allAppointments);
      
      if (allAppointments && allAppointments.length > 0) {
        // Format appointments as calendar events
        const calendarEvents = allAppointments.map(apt => {
          // Create a date object for the appointment
          const apptTime = apt.starttime; // e.g., "09:00"
          const [hours, minutes] = apptTime ? apptTime.split(':').map(Number) : [0, 0]; // Define hours/minutes
          let apptDate = new Date(apt.date); // e.g., "2025-03-26"
          apptDate.setHours(hours, minutes, 0, 0); // Set hours and minutes
          
          // --- BEGIN ADDED LOGGING ---
          // console.log(`Processing apt ID: ${apt.appointmentid}`);
          // console.log(`  Raw Date: ${apt.date}, Raw StartTime: ${apt.starttime}`);
          // console.log(`  Parsed H/M: ${hours}:${minutes}`);
          // console.log(`  Created apptDate Object: ${apptDate.toString()}`);
          // --- END ADDED LOGGING ---
          
          // Format time for display (exact appointment time) - Define formattedTime
          const formattedTime = apptDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });

          // Get patient name
          const patientName = apt.patient 
            ? `${apt.patient.firstname} ${apt.patient.lastname}`
            : 'Unknown Patient';
          
          // Get provider name - first try from appointment data, then lookup in providers list
          let providerName = apt.providername;
          
          if (!providerName && apt.provider) {
            providerName = `${apt.provider.firstname || ''} ${apt.provider.lastname || ''}`.trim();
          }
          
          // Debug logging for provider lookup (can be removed once working)
          console.log(`Debugging provider for apt ${apt.appointmentid}:`, {
            aptProviderId: apt.providerid,
            aptProviderName: apt.providername,
            aptProvider: apt.provider,
            providersLength: providers.length,
            currentProviderName: providerName
          });
          
          // If still no provider name, lookup in the providers list
          // Note: Use the providers state directly - it should be populated by now
          if (!providerName && apt.providerid) {
            // Try to find the provider in the current providers state
            const provider = providers.find(p => 
              p.providerid?.toString() === apt.providerid?.toString()
            );
            console.log(`Provider lookup for ID ${apt.providerid}:`, {
              providersAvailable: providers.length,
              foundProvider: provider,
              allProviderIds: providers.map(p => p.providerid)
            });
            
            if (provider) {
              if (provider.displayname === 'IV_HOME') {
                providerName = 'IV Home';
              } else if (provider.displayname === 'IV_OFFICE') {
                providerName = 'IV Office';
              } else {
                providerName = `${provider.firstname || ''} ${provider.lastname || ''}`.trim();
                if (!providerName) {
                  providerName = provider.displayname || `Provider ${apt.providerid}`;
                }
              }
              console.log(`Resolved provider name: ${providerName}`);
            } else {
              console.log(`Provider ID ${apt.providerid} not found in providers list`);
            }
          }
          
          // Final fallback
          if (!providerName) {
            providerName = `Provider ${apt.providerid || 'Unknown'}`;
          }

          // Extract appointment notes
          let notesText = '';
          if (apt.appointmentnotes) {
            if (Array.isArray(apt.appointmentnotes)) {
              const notesArray = apt.appointmentnotes.map(note => 
                typeof note === 'object' && note.text ? note.text : note
              );
              notesText = notesArray.join(', ');
            } else if (typeof apt.appointmentnotes === 'string') {
              notesText = apt.appointmentnotes;
            } else if (typeof apt.appointmentnotes === 'object' && apt.appointmentnotes.text) {
              notesText = apt.appointmentnotes.text;
            }
          } else if (apt.notes) {
            notesText = apt.notes;
          }

          // Create event object with all necessary fields for tooltip
          return {
            id: apt.appointmentid || `apt-${Math.random().toString(36).substr(2, 9)}`,
            date: apptDate,
            day: apptDate.getDate(),
            hour: apptDate.getHours(),
            minute: apptDate.getMinutes(),
            title: patientName,
            time: formattedTime,
            phone: apt.patient?.mobilephone || apt.patient?.homephone || '',
            type: apt.appointmenttype || "Appointment",
            status: apt.appointmentstatus || "booked",
            duration: apt.duration || null,
            notes: notesText || null,
            providerName: providerName,
            cancelreasonname: apt.cancelreasonname || null,
            color: getRandomColor()
          };
        });
        
        console.log("[Calendar] Events formatted:", calendarEvents.length);
        
        // Cache the results
        eventsCacheRef.current.set(weekKey, calendarEvents);
        console.log('[Calendar] Cached week:', weekKey);
        
        setEvents(calendarEvents);
        
        // Prefetch adjacent weeks in background (non-blocking)
        prefetchAdjacentWeeks(fetchStart);
      } else {
        // Cache empty result too
        eventsCacheRef.current.set(weekKey, []);
        setEvents([]);
      }
    } catch (error) {
      console.error("Error fetching calendar events:", error);
      setEvents([]);
    }
    setLoading(false);
  }, [weekStart, selectedDepartment, selectedProvider, selectedLocation, departmentsByLocation, providers, getWeekKey]);
  
  // Use a ref to prevent duplicate API calls
  const initialLoadRef = useRef(false);
  const fetchInProgressRef = useRef(false);
  
  // Fetch events when component mounts or filters change
  useEffect(() => {
    // Prevent duplicate API calls
    if (fetchInProgressRef.current) {
      console.log("Calendar: Skipping duplicate fetch call - fetch already in progress");
      return;
    }
    
    // For initial load or specific trigger conditions
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      fetchInProgressRef.current = true;
      fetchCalendarEvents().finally(() => {
        fetchInProgressRef.current = false;
      });
    } else {
      // Add a small delay to prevent rapid consecutive calls
      fetchInProgressRef.current = true;
      setTimeout(() => {
        fetchCalendarEvents().finally(() => {
          fetchInProgressRef.current = false;
        });
      }, 100);
    }
  }, [fetchCalendarEvents]);
  
  // Format the date range for display (Mar 24, 2025 - Mar 30, 2025)
  const formatDateRange = () => {
    const start = weekDays[0];
    const end = weekDays[weekDays.length - 1];
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };
  
  // Get the time as a string key for mapping appointments to slots (e.g., "9:00 AM")
  const getTimeSlotKey = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    const minuteStr = minutes === 0 ? '00' : minutes === 30 ? '30' : minutes.toString();
    return `${hour12}:${minuteStr} ${ampm}`;
  };

  const getAppointments = (day, timeSlot) => {
    try {
      // Extract just the day part for comparison
      const dayDate = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      const dayTimestamp = dayDate.setHours(0, 0, 0, 0);
      
      // Parse the time slot (e.g. "9:00 AM")
      const [hourStr, rest] = timeSlot.split(':');
      const [minuteStr, ampm] = rest.split(' ');
      
      let slotHour = parseInt(hourStr);
      const slotMinute = parseInt(minuteStr);
      
      // Convert 12-hour format to 24-hour for the slot
      if (ampm === 'PM' && slotHour < 12) {
        slotHour += 12;
      } else if (ampm === 'AM' && slotHour === 12) {
        slotHour = 0; // Midnight case
      }
      
      // Only log when debug is needed
      // console.log(`--- Checking Slot: ${timeSlot} (${slotHour}:${slotMinute}) on Day: ${day.toDateString()} ---`);

      // Filter events for this specific day and time slot
      return events.filter(event => {
        try {
          // Skip if event doesn't have a date or title
          if (!event.date || !event.title) {
            console.log(`Skipping event without date/title: ${JSON.stringify(event)}`);
            return false;
          }
          
          // Extract the date part of the event date
          const eventDate = new Date(event.date);
          const eventDayTimestamp = new Date(
            eventDate.getFullYear(), 
            eventDate.getMonth(), 
            eventDate.getDate()
          ).setHours(0, 0, 0, 0);
          
          // Check if the event is on the same day
          if (eventDayTimestamp !== dayTimestamp) {
            return false;
          }
          
          // Get the event's hour and minute
          const eventHour = eventDate.getHours();
          const eventMinute = eventDate.getMinutes();
          
          // For time-specific matching:
          // 1. Exact match (e.g., 9:00 event in 9:00 slot)
          // 2. 30-minute match (e.g., 9:30 event in 9:30 slot)
          // 3. For irregular times (e.g., 9:15), match to nearest slot:
          //    - 9:01-9:29 maps to 9:00 slot
          //    - 9:31-9:59 maps to 9:30 slot
          let match = false;
          
          if (eventHour === slotHour) {
            // For events that start exactly at 0 or 30 minutes, match exactly
            if ((eventMinute === 0 && slotMinute === 0) || 
                (eventMinute === 30 && slotMinute === 30)) {
              match = true;
            }
            // For other minutes, match to the nearest slot
            else if (eventMinute > 0 && eventMinute < 30 && slotMinute === 0) {
              match = true;
            }
            else if (eventMinute > 30 && eventMinute < 60 && slotMinute === 30) {
              match = true;
            }
          }
          
          // Only log matches when debugging is needed
          // if (match) {
          //   console.log(`  MATCH FOUND: Event ${event.title} matches slot ${timeSlot}`);
          // }
          
          return match;
          
        } catch (err) {
          // console.error(`Error filtering event ${event.id || 'unknown'}:`, err, event);
          return false;
        }
      });
    } catch (err) {
      console.error("Error in getAppointments:", err);
      return [];
    }
  };

  const handleAppointmentClick = (appointment) => {
    console.log("Appointment clicked:", appointment);
    // You can add functionality to view or edit the appointment details
  };
  
  // We no longer use a global loading indicator that replaces the entire UI

  return (
    <Box sx={{ p: 0, bgcolor: theme.palette.background.default, width: '100%', minHeight: '100vh', position: 'relative' }}>
      {/* Loading overlay that doesn't hide the UI */}
      {loading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 1000,
        }}>
          <CircularProgress sx={{ color: theme.palette.primary.main, mb: 2 }} />
          <Typography variant="body2" color="textSecondary">Loading calendar...</Typography>
        </Box>
      )}
      {/* Header with Provider selection and Date navigation */}
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
            <InputLabel 
              id="location-select-label"
              shrink={true}
            >
              Location
            </InputLabel>
            <Select
              labelId="location-label"
              value={selectedLocation}
              onChange={handleLocationChange}
              label="Location"
              displayEmpty
              renderValue={(value) => value === "" ? "Default Location" : value}
            >
              <MenuItem value="">Default Location</MenuItem>
              {Object.keys(departmentsByLocation).map((locationName) => (
                <MenuItem key={locationName} value={locationName}>
                  {locationName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 ,display: 'none'}}>
            <InputLabel 
              id="department-select-label"
              shrink={true}
            >
              Department
            </InputLabel>
            <Select
              labelId="department-label"
              value={selectedDepartment}
              onChange={handleDepartmentChange}
              label="Department"
              disabled={!selectedLocation}
              displayEmpty
              renderValue={(value) => {
                if (value === "") return "All Departments";
                const dept = departments.find(d => d.departmentid.toString() === value.toString());
                return dept ? `${dept.name}` : "Department";
              }}
            >
              <MenuItem value="">All Departments</MenuItem>
              {selectedLocation 
                ? (departmentsByLocation[selectedLocation] || []).map(department => (
                    <MenuItem key={department.departmentid} value={department.departmentid.toString()}>
                      {department.name}
                    </MenuItem>
                  ))
                : departments.map(department => (
                    <MenuItem key={department.departmentid} value={department.departmentid.toString()}>
                      {department.name}
                    </MenuItem>
                  ))
              }  
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
            <InputLabel 
              id="provider-select-label"
              shrink={true}
            >
              Provider
            </InputLabel>
            <Select
              labelId="provider-label"
              value={selectedProvider}
              onChange={handleProviderChange}
              label="Provider"
              disabled={!selectedLocation}
              displayEmpty
              renderValue={(value) => {
                if (value === '') return "All Providers";
                const provider = providers.find(p => p.providerid.toString() === value.toString());
                return provider ? formatProviderName(provider) : "Providers";
              }}
            >
              <MenuItem value="">All Providers</MenuItem>
              {providers.map(provider => (
                <MenuItem key={provider.providerid} value={provider.providerid.toString()}>
                  {formatProviderName(provider)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Date navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={goToPreviousWeek} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2" sx={{ mx: 2 }}>
            {formatDateRange()}
          </Typography>
          <IconButton onClick={goToNextWeek} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
      
      {/* Appointment filter buttons */}
      <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
        <Button
          variant={selectedFilter === 'all' ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => handleFilterChange('all')}
          sx={{ mr: 1 }}
        >
          All
        </Button>
        <Button
          variant={selectedFilter === 'booked' ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => handleFilterChange('booked')}
          sx={{ mr: 1 }}
        >
          Booked
        </Button>
        <Button
          variant={selectedFilter === 'open' ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => handleFilterChange('open')}
        >
          Open
        </Button>
      </Box>

      {/* Calendar container with scrollable content */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: 'calc(100vh - 180px)', 
        overflow: 'hidden'
      }}>
        {/* Fixed header row with days */}
        <Box sx={{ 
          display: 'flex', 
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: '#f5f5f5',
          zIndex: 5
        }}>
          {/* Empty cell for time column header */}
          <Box sx={{ 
            width: '60px', 
            height: '50px', 
            borderRight: `1px solid ${theme.palette.divider}`,
            flexShrink: 0 
          }}></Box>
          
          {/* Day headers */}
          {weekDays.map((day, dayIndex) => (
            <Box 
              key={dayIndex} 
              sx={{ 
                flex: 1,
                height: '50px',
                p: 1,
                borderRight: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {day.getDate()}
              </Typography>
            </Box>
          ))}
        </Box>
        
        {/* Scrollable area with time slots and appointments */}
        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'auto' }}>
          {/* Time column */}
          <Box 
            sx={{ 
              width: '60px', 
              borderRight: `1px solid ${theme.palette.divider}`,
              flexShrink: 0
            }}
          >
            {/* Time labels */}
            {timeSlots.map((time, index) => (
              <Box 
                key={index} 
                sx={{ 
                  height: '130px', 
                  borderBottom: 'none',  
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  },
                  borderTop: index === 0 ? 'none' : '1px dashed rgba(0,0,0,0.03)' 
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 'medium' }}>{time}</Typography>
              </Box>
            ))}
          </Box>
          
          {/* Days of the week */}
          {weekDays.map((day, dayIndex) => (
            <Box 
              key={dayIndex} 
              sx={{ 
                flex: 1,
                borderRight: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Time slots for this day */}
              {timeSlots.map((timeSlot, slotIndex) => {
                const appointments = getAppointments(day, timeSlot);
                
                return (
                  <Box 
                    key={slotIndex} 
                    sx={{ 
                      height: '130px', 
                      borderBottom: 'none',  
                      position: 'relative',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.02)'
                      },
                      borderTop: slotIndex === 0 ? 'none' : '1px dashed rgba(0,0,0,0.03)' 
                    }}
                  >
                    {appointments.map((appointment, index) => {
                      // Get date object from appointment
                      const appointmentDate = new Date(appointment.date);
                      const hours = appointmentDate.getHours();
                      const minutes = appointmentDate.getMinutes();
                      
                      // Round to nearest 30-minute slot (8:00, 8:30, 9:00, etc.)
                      const hourFormatted = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                      const minuteFormatted = minutes >= 30 ? "30" : "00";
                      const ampm = hours >= 12 ? "PM" : "AM";
                      
                      // Create the exact time string format that matches our timeSlots array
                      const timeString = `${hourFormatted}:${minuteFormatted} ${ampm}`;
                      
                      // Find this exact time in our timeSlots array
                      const timeIndex = timeSlots.indexOf(timeString);
                      
                      // console.log(`Mapping appointment: ${appointment.title} from ${hours}:${minutes.toString().padStart(2, '0')} to "${timeString}" (index ${timeIndex})`);
                      
                      // Calculate top position (130px per time slot)
                      // Default to 0 if time not found (as a safety)
                      const topPosition = timeIndex >= 0 ? timeIndex * 100 : 0;
                      
                      // Find other appointments in this same time slot for side-by-side placement
                      const sameTimeAppointments = appointments.filter(app => {
                        if (app.id === appointment.id) return true; // Include this appointment
                        
                        const appDate = new Date(app.date);
                        const appHour = appDate.getHours();
                        const appMinute = appDate.getMinutes();
                        
                        // Check if same time slot when rounded to nearest half hour
                        return appHour === hours && 
                               (appMinute >= 30) === (minutes >= 30);
                      });
                      
                      const position = sameTimeAppointments.findIndex(app => app.id === appointment.id);
                      const total = sameTimeAppointments.length;
                      const width = total > 1 ? `${98 / total}%` : '98%';
                      const left = total > 1 ? `${(position * 98) / total}%` : '1%';
                      
                      return (
                        <Box
                          key={appointment.id}
                          sx={{
                            position: 'absolute',
                            top: `${topPosition}px`,
                            left: left,
                            width: width,
                            height: '100px', // Slightly shorter to prevent overlap
                            backgroundColor: appointment.color || '#3788d8',
                            color: '#fff',
                            p: 1,
                            cursor: 'pointer',
                            overflow: 'hidden',
                            borderRadius: '4px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                            display: 'flex',
                            flexDirection: 'column',
                            zIndex: 1, // Base z-index for all appointment cards
                            '&:hover': {
                              boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                              zIndex: 1000, // Higher z-index on hover to ensure hover works for top cards
                              transform: 'scale(1.02)' // Slight scale to make hover more visible
                            }
                          }}
                          onClick={() => handleAppointmentClick(appointment)}
                          // Use onMouseEnter and onMouseLeave for custom tooltip behavior
                          onMouseEnter={(e) => {
                            // Find or create tooltip container
                            let tooltipContainer = document.getElementById('appointment-tooltip');
                            if (!tooltipContainer) {
                              tooltipContainer = document.createElement('div');
                              tooltipContainer.id = 'appointment-tooltip';
                              tooltipContainer.style.position = 'fixed';
                              tooltipContainer.style.backgroundColor = 'rgba(0,0,0,0.9)';
                              tooltipContainer.style.color = 'white';
                              tooltipContainer.style.padding = '10px';
                              tooltipContainer.style.borderRadius = '4px';
                              tooltipContainer.style.zIndex = '9999';
                              tooltipContainer.style.maxWidth = '250px';
                              tooltipContainer.style.boxShadow = '0 3px 6px rgba(0,0,0,0.2)';
                              document.body.appendChild(tooltipContainer);
                            }
                            
                            // Map appointment status to complete description using same logic as appointments component
                            let statusDescription = "Booked";
                            let statusIcon = "📅";
                            
                            // Check appointment.status (which contains the raw status code from calendar events)
                            if (appointment.status) {
                              switch(appointment.status) {
                                case 'x':
                                  if (appointment.cancelreasonname === 'PATIENT NO SHOW') {
                                    statusDescription = "No Show";
                                    statusIcon = "❌";
                                  } else {
                                    statusDescription = "Cancelled";
                                    statusIcon = "🚫";
                                  }
                                  break;
                                case 'f': 
                                  statusDescription = "Scheduled";
                                  statusIcon = "📅";
                                  break;
                                case 'o':
                                  statusDescription = "Open";
                                  statusIcon = "🔓";
                                  break;
                                case '2':
                                  statusDescription = "Checked In";
                                  statusIcon = "✅";
                                  break;
                                case '3':
                                  statusDescription = "Checked Out";
                                  statusIcon = "🏁";
                                  break;
                                case '4':
                                  statusDescription = "Charge Entered";
                                  statusIcon = "💰";
                                  break;
                                default:
                                  // Handle string status descriptions for backward compatibility
                                  if (appointment.status === 'cancelled') statusDescription = "Cancelled";
                                  else if (appointment.status === 'completed') statusDescription = "Completed";
                                  else if (appointment.status === 'checked-in') statusDescription = "Checked In";
                                  else if (appointment.status === 'booked') statusDescription = "Booked";
                                  else statusDescription = appointment.status; // Use as-is if it's already a descriptive string
                              }
                            }

                            // Generate tooltip content with enhanced status information
                            tooltipContainer.innerHTML = `
                              <div style="font-weight: bold; margin-bottom: 8px; font-size: 14px; border-bottom: 1px solid rgba(255,255,255,0.2); padding-bottom: 4px;">${appointment.title}</div>
                              <div style="display: flex; align-items: center; font-size: 12px; margin-bottom: 4px;">
                                <span style="margin-right: 8px;">⏰</span> <strong>Time:</strong> ${appointment.time}
                              </div>
                              <div style="display: flex; align-items: center; font-size: 12px; margin-bottom: 4px;">
                                <span style="margin-right: 8px;">${statusIcon}</span> <strong>Status:</strong> ${statusDescription}
                              </div>
                              ${appointment.type ? `
                                <div style="display: flex; align-items: center; font-size: 12px; margin-bottom: 4px;">
                                  <span style="margin-right: 8px;">🏥</span> <strong>Type:</strong> ${appointment.type}
                                </div>
                              ` : ''}
                              ${appointment.providerName ? `
                                <div style="display: flex; align-items: center; font-size: 12px; margin-bottom: 4px;">
                                  <span style="margin-right: 8px;">👤</span> <strong>Provider:</strong> ${appointment.providerName}
                                </div>
                              ` : ''}
                              ${appointment.duration ? `
                                <div style="display: flex; align-items: center; font-size: 12px; margin-bottom: 4px;">
                                  <span style="margin-right: 8px;">🕒</span> <strong>Duration:</strong> ${appointment.duration} min
                                </div>
                              ` : ''}
                              ${appointment.notes && appointment.notes !== 'No reason specified' ? `
                                <div style="display: flex; align-items: flex-start; font-size: 12px; margin-top: 6px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.2);">
                                  <span style="margin-right: 8px; margin-top: 1px;">📝</span> <strong>Notes:</strong> ${appointment.notes}
                                </div>
                              ` : ''}
                            `;
                            
                            // Position tooltip near mouse
                            tooltipContainer.style.display = 'block';
                            tooltipContainer.style.left = `${e.clientX + 15}px`;
                            tooltipContainer.style.top = `${e.clientY - 10}px`;
                            
                            // Prevent tooltip from going off-screen
                            const rect = tooltipContainer.getBoundingClientRect();
                            if (rect.right > window.innerWidth) {
                              tooltipContainer.style.left = `${e.clientX - rect.width - 15}px`;
                            }
                            if (rect.bottom > window.innerHeight) {
                              tooltipContainer.style.top = `${e.clientY - rect.height - 15}px`;
                            }
                          }}
                          onMouseLeave={() => {
                            // Hide tooltip
                            const tooltipContainer = document.getElementById('appointment-tooltip');
                            if (tooltipContainer) {
                              tooltipContainer.style.display = 'none';
                            }
                          }}
                          onMouseMove={(e) => {
                            // Move tooltip with cursor
                            const tooltipContainer = document.getElementById('appointment-tooltip');
                            if (tooltipContainer && tooltipContainer.style.display !== 'none') {
                              tooltipContainer.style.left = `${e.clientX + 15}px`;
                              tooltipContainer.style.top = `${e.clientY - 10}px`;
                              
                              // Prevent tooltip from going off-screen
                              const rect = tooltipContainer.getBoundingClientRect();
                              if (rect.right > window.innerWidth) {
                                tooltipContainer.style.left = `${e.clientX - rect.width - 15}px`;
                              }
                              if (rect.bottom > window.innerHeight) {
                                tooltipContainer.style.top = `${e.clientY - rect.height - 15}px`;
                              }
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ 
                              fontSize: '0.7rem', 
                              fontWeight: 'bold',
                              bgcolor: 'rgba(0,0,0,0.2)',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: '2px'
                            }}>
                              {appointment.time}
                            </Typography>
                            {appointment.status && (
                              <Typography variant="caption" sx={{ 
                                fontSize: '0.65rem', 
                                bgcolor: 'rgba(0,0,0,0.15)',
                                px: 0.5,
                                py: 0.25,
                                borderRadius: '2px'
                              }}>
                                {appointment.status}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 'bold', 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            fontSize: '0.85rem',
                            color: 'rgba(255,255,255,0.95)' // Brighter white for patient names
                          }}>
                            {appointment.title}
                          </Typography>
                          {appointment.type && (
                            <Typography variant="caption" sx={{ 
                              fontSize: '0.7rem', 
                              mt: 'auto',
                              alignSelf: 'flex-start'
                            }}>
                              {appointment.type}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [companyName] = useState("Elevate Health Group");
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});

  useEffect(() => {
    const initDb = async () => {
      try {
        const result = await dbInitService.initialize();
        console.log('Database initialization complete:', result.success ? 'Success' : 'Failed');
      } catch (error) {
        console.error('Error initializing database:', error);
      }
    };
    
    initDb();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigation even if logout fails
      navigate('/login');
    }
  };

  const navItems = [
    { name: "Insights", path: "insights", icon: LineChartIcon },
    { name: "Patients", path: "patients", icon: UsersIcon },
    { name: "Appointments", path: "appointments", icon: ClockIcon },
    { name: "Calendar", path: "calendar", icon: CalendarIcon },
    { name: "Public Reputation", path: "public-reputation", icon: StarIcon },
    { name: "Private Feedback", path: "private-feedback", icon: MessageCircleIcon },
    { name: "Concierge Medicine", path: "concierge-medicine", icon: AssignmentIcon },
    { 
      name: "Settings", 
      path: "settings", 
      icon: SettingsIcon,
      subItems: [
        { name: "Insurance Manager", path: "settings/insurance", icon: LocalHospitalIcon },
        { name: "Availability", path: "settings/availability", icon: AccessTimeIcon },
        { name: "Checkout Manager", path: "settings/checkout-manager", icon: CheckCircleIcon },
        { name: "Locations Manager", path: "settings/locations", icon: LocationIcon },
        { name: "Team Manager", path: "settings/team", icon: BusinessIcon },
        { name: "Reviews Manager", path: "settings/reviews", icon: StarIcon },
      ]
    },
  ];

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <SidebarContainer>
        <BrandContainer>
          <Avatar sx={{ bgcolor: '#383352', mr: 2 }}>T</Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {companyName}
            </Typography>
          </Box>
          <Box 
            component="span" 
            sx={{ 
              cursor: 'pointer',
              position: 'absolute',
              right: '10px',
              top: '20px'
            }}
          >
            <XIcon fontSize="small" />
          </Box>
        </BrandContainer>

        <Box sx={{ flex: 1 }}>
          {navItems.map((item) => (
            <React.Fragment key={item.path}>
              {item.subItems ? (
                // For parent items with subitems, make them toggle expand/collapse
                <div>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 24px',
                      color: location.pathname.includes(`/admin/${item.path.split('/')[0]}`) 
                        ? theme => theme.palette.primary.main 
                        : theme => theme.palette.text.primary,
                      fontWeight: location.pathname.includes(`/admin/${item.path.split('/')[0]}`) 
                        ? 'bold' 
                        : 'normal',
                      backgroundColor: location.pathname.includes(`/admin/${item.path.split('/')[0]}`) 
                        ? 'rgba(0, 0, 0, 0.04)' 
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        cursor: 'pointer'
                      },
                    }}
                    onClick={() => {
                      setExpandedMenus(prev => ({
                        ...prev,
                        [item.path]: !prev[item.path]
                      }));
                    }}
                  >
                    <item.icon fontSize="small" style={{ marginRight: 10 }} />
                    {item.name}
                    <ExpandMoreIcon 
                      fontSize="small" 
                      style={{ 
                        marginLeft: 'auto',
                        transform: expandedMenus[item.path] ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s'
                      }} 
                    />
                  </Box>
                  
                  {expandedMenus[item.path] && (
                    <Box sx={{ ml: 3 }}>
                      {item.subItems.map((subItem) => (
                        <NavItem
                          key={subItem.path}
                          to={`/admin/${subItem.path}`}
                          className={({ isActive }) => (isActive ? "active" : "")}
                        >
                          <subItem.icon fontSize="small" style={{ marginRight: 10 }} />
                          {subItem.name}
                        </NavItem>
                      ))}
                    </Box>
                  )}
                </div>
              ) : (
                // For regular items without subitems
                <NavItem
                  to={`/admin/${item.path}`}
                  className={({ isActive }) => (isActive ? "active" : "")}
                >
                  <item.icon fontSize="small" style={{ marginRight: 10 }} />
                  {item.name}
                </NavItem>
              )}
            </React.Fragment>
          ))}
        </Box>

        <UserContainer>
          <Avatar sx={{ bgcolor: '#383352', mr: 1, width: 32, height: 32 }}>
            {user?.email?.charAt(0).toUpperCase() || 'A'}
          </Avatar>
          <Box sx={{ 
            flex: 1, 
            minWidth: 0, // Allow text to shrink
            mr: 1 
          }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ 
                fontSize: '0.8125rem',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                fontSize: '0.75rem',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block'
              }}
            >
              {user?.email || 'admin@elevatehealth.com'}
            </Typography>
          </Box>
          <IconButton 
            onClick={handleLogout}
            size="small"
            sx={{ 
              color: 'text.secondary',
              flexShrink: 0, // Don't shrink the button
              width: 32,
              height: 32,
              '&:hover': {
                color: 'error.main',
                backgroundColor: 'error.light'
              }
            }}
            title="Logout"
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </UserContainer>
      </SidebarContainer>

      <ContentContainer>
        <RouteContainer>
          <Routes>
            <Route path="insights" element={<Insights />} />
            <Route path="public-reputation" element={<FeedbackPublic />} />
            <Route path="private-feedback" element={<FeedbackPrivate />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="patients" element={<Patients />} />
            <Route path="concierge-medicine" element={<ConsultationDashboard />} />
            <Route path="calendar" element={<CalendarView />} />
            <Route path="settings" element={<div>Settings Component</div>} />
            <Route path="settings/insurance" element={<InsuranceManager />} />
            <Route path="settings/availability" element={<AvailabilityManager />} />
            <Route path="settings/checkout-manager" element={<CheckoutFeedbackManager />} />
            <Route path="settings/locations" element={<LocationsManager />} />
            <Route path="settings/team" element={<TeamManager />} />
            <Route path="settings/reviews" element={<AdminReviewsPage />} />
            <Route path="*" element={<Insights />} />
          </Routes>

          {/* Always render the CheckoutFeedbackManager in the background */}
          <div style={{ display: 'none' }}>
            <CheckoutFeedbackManager />
          </div>
        </RouteContainer>
      </ContentContainer>
    </Box>
  );
};

export default AdminDashboard;
